import Phaser from 'phaser';
import { GAME_CONFIG, OrbColor } from '../config/GameConfig';
import { ModuleRenderer, ModuleCardOptions } from '../ui/ModuleRenderer';
import { loadTheme, getTheme, UIBuilder, Theme } from '../ui/UITheme';
import { ShipSprite } from '../ui/ShipSprite';
import {
    getModulesData,
    getModuleDefinition,
    getEvolutionRecipe,
    getCommonModules,
    PlayerModule,
    ModuleDefinition,
    loadModuleData,
    loadSymbolsData,
    loadEffectsData,
} from '../data/ModuleData';
import { apiClient, FittedModule as ApiFittedModule } from '../network/ApiClient';
import { loadEnemyData, ASPECT_COLORS, getThreatMultipliers } from '../data/EnemyData';
import { RunPlan, generateRun } from '../data/RunGenerator';
import { rollAdditionalAspect } from '../utils/aspectUtils';

interface FittedModule {
    slotIndex: number;
    slotType: 'weapon' | 'support';
    module: PlayerModule | null;
}

export class BaseScene extends Phaser.Scene {
    // Layout constants
    private readonly LEFT_WIDTH: number;
    private readonly MIDDLE_WIDTH: number;
    private readonly RIGHT_WIDTH: number;
    private readonly LEFT_X: number;
    private readonly MIDDLE_X: number;
    private readonly RIGHT_X: number;
    private readonly PANEL_PADDING = 16;

    // State
    private inventory: PlayerModule[] = [];
    private fittedModules: FittedModule[] = [];
    private selectedModule: PlayerModule | null = null;
    private threatLevel = 1;
    private runPlan: RunPlan | null = null;
    private currentSort: 'rarity' | 'color' | 'level' | 'type' = 'rarity';
    private sortReversed = false;
    private sortButtonsContainer!: Phaser.GameObjects.Container;
    private playerScrap = 0;
    private weaponBlueprints = 0;
    private supportBlueprints = 0;
    private scrapText!: Phaser.GameObjects.Text;
    private blueprintText!: Phaser.GameObjects.Text;
    private isGuest = false;

    // UI Components
    private moduleRenderer!: ModuleRenderer;
    private ui!: UIBuilder;
    private theme!: Theme;

    // Left panel components
    private selectedModuleContainer!: Phaser.GameObjects.Container;
    private weaponGridContainer!: Phaser.GameObjects.Container;
    private supportGridContainer!: Phaser.GameObjects.Container;
    private weaponGradientTop!: Phaser.GameObjects.Graphics;
    private weaponGradientBottom!: Phaser.GameObjects.Graphics;
    private supportGradientTop!: Phaser.GameObjects.Graphics;
    private supportGradientBottom!: Phaser.GameObjects.Graphics;

    // Grid bounds for scroll detection
    private weaponGridBounds!: Phaser.Geom.Rectangle;
    private supportGridBounds!: Phaser.Geom.Rectangle;

    // Middle panel components
    private shipSprite!: ShipSprite;
    private statsContainer!: Phaser.GameObjects.Container;
    private slotsContainer!: Phaser.GameObjects.Container;
    private connectionLines!: Phaser.GameObjects.Graphics;

    // Right panel components
    private mapContainer!: Phaser.GameObjects.Container;
    private controlsContainer!: Phaser.GameObjects.Container;
    private mapBounds!: Phaser.Geom.Rectangle;
    private mapMask!: Phaser.GameObjects.Graphics;
    private mutatorButtons: Phaser.GameObjects.Container[] = [];
    private selectedMutators: Set<string> = new Set();
    private threatSliderBg!: Phaser.GameObjects.Graphics;
    private threatText!: Phaser.GameObjects.Text;
    private rerollButton!: Phaser.GameObjects.Container;
    private rerollLabel!: Phaser.GameObjects.Text;
    private rerollBg!: Phaser.GameObjects.Graphics;
    private startRunButton!: Phaser.GameObjects.Container;
    private startRunLabel!: Phaser.GameObjects.Text;
    private startRunBg!: Phaser.GameObjects.Graphics;

    // Grid scroll state
    private weaponScrollY = 0;
    private supportScrollY = 0;
    private weaponMaxScrollY = 0;
    private supportMaxScrollY = 0;
    private weaponGridBaseY = 0;
    private supportGridBaseY = 0;

    // Momentum scrolling
    private scrollVelocity = 0;
    private activeScrollGrid: 'weapon' | 'support' | null = null;
    private readonly SCROLL_FRICTION = 0.92;
    private readonly MIN_VELOCITY = 0.5;

    // Grid layout constants
    private readonly GRID_COLS = 4;
    private gridCellSize = 0;
    private gridCellGap = 8;
    private gridVisibleHeight = 0;

    // Unified pointer state
    private pointerDownTime = 0;
    private pointerDownY = 0;
    private pointerDownX = 0;
    private pointerMoveDistance = 0;
    private lastPointerY = 0;
    private isPointerDown = false;
    private pointerDownTarget: Phaser.GameObjects.Container | null = null;
    private pointerDownGrid: 'weapon' | 'support' | null = null;

    // Drag-to-equip state
    private draggedModuleCard: Phaser.GameObjects.Container | null = null;
    private draggedModule: PlayerModule | null = null;
    private draggedFromSlot: FittedModule | null = null; // Track if dragging from a fitted slot
    private isDragging = false;
    private dragDisabled = false;  // True if vertical movement detected during hold
    private readonly DRAG_THRESHOLD = 25;  // Distance before drag starts
    private readonly DRAG_HOLD_TIME = 200; // ms to hold before drag can start
    private readonly DRAG_VERTICAL_TOLERANCE = 8; // Max vertical movement during hold to allow drag
    private readonly CLICK_THRESHOLD = 10;

    // Slot containers for drop detection
    private slotContainers: Phaser.GameObjects.Container[] = [];

    constructor() {
        super({ key: 'BaseScene' });

        this.LEFT_WIDTH = GAME_CONFIG.WIDTH * 0.30;
        this.MIDDLE_WIDTH = GAME_CONFIG.WIDTH * 0.40;
        this.RIGHT_WIDTH = GAME_CONFIG.WIDTH * 0.30;
        this.LEFT_X = 0;
        this.MIDDLE_X = this.LEFT_WIDTH;
        this.RIGHT_X = this.LEFT_WIDTH + this.MIDDLE_WIDTH;
    }

    async create(): Promise<void> {
        await loadTheme();
        this.theme = getTheme();
        this.ui = new UIBuilder(this);

        await loadModuleData();
        await loadSymbolsData();
        await loadEffectsData();
        await loadEnemyData();

        // Generate initial run plan
        this.runPlan = generateRun(this.threatLevel);

        this.moduleRenderer = new ModuleRenderer(this);

        const gridWidth = this.LEFT_WIDTH - 48;
        this.gridCellSize = Math.floor((gridWidth - this.gridCellGap * 3) / this.GRID_COLS);

        // Initialize empty inventory - will be loaded from server
        this.inventory = [];
        this.initializeFittedSlots();

        // Load player inventory first (scrap, blueprints, modules)
        await this.loadPlayerInventory();

        // Then load saved loadout (needs inventory to match modules)
        await this.loadSavedLoadout();

        this.createLeftPanel();
        this.createMiddlePanel();
        this.createRightPanel();
        this.setupInput();

        this.updateSelectedModuleDisplay();
        this.updateWeaponGrid();
        this.updateSupportGrid();
        this.updateShipVisual();
        this.updateFittingSlots();
        this.updateMapPreview();
    }

    private async loadSavedLoadout(): Promise<void> {
        try {
            const user = await apiClient.getMe();
            if (!user) return;

            const loadout = await apiClient.getLoadout();
            if (loadout && loadout.data.fittedModules) {
                // Restore fitted modules from saved loadout
                for (const savedModule of loadout.data.fittedModules) {
                    if (savedModule.module) {
                        const slot = this.fittedModules.find(
                            fm => fm.slotIndex === savedModule.slotIndex && fm.slotType === savedModule.slotType
                        );
                        if (slot) {
                            // Find matching module in inventory
                            const inventoryModule = this.inventory.find(
                                m => m.definitionId === savedModule.module!.definitionId
                            );
                            if (inventoryModule) {
                                slot.module = inventoryModule;
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load saved loadout:', error);
        }
    }

    private async loadPlayerInventory(): Promise<void> {
        try {
            const user = await apiClient.getMe();
            if (!user) {
                // Guest mode: provide starter common modules but no scrap/blueprints
                console.log('Guest mode: loading starter modules');
                this.playerScrap = 0;
                this.weaponBlueprints = 0;
                this.supportBlueprints = 0;
                this.inventory = getCommonModules();
                this.isGuest = true;
                return;
            }

            const inventory = await apiClient.getInventory();
            this.playerScrap = inventory.scrap || 0;
            this.weaponBlueprints = inventory.weaponBlueprints || 0;
            this.supportBlueprints = inventory.supportBlueprints || 0;
            this.threatLevel = inventory.threatLevel || 1;
            this.isGuest = false;

            // Load modules from server (starter modules granted automatically if empty)
            if (inventory.modules && inventory.modules.length > 0) {
                this.inventory = inventory.modules.map((m: any) => ({
                    definitionId: m.definitionId,
                    instanceId: m.instanceId,
                    level: m.level,
                    aspects: m.aspects || [],
                }));
            }
        } catch (error) {
            console.error('Failed to load player inventory:', error);
            // On error, provide starter modules so guest can still play
            this.playerScrap = 0;
            this.weaponBlueprints = 0;
            this.supportBlueprints = 0;
            this.inventory = getCommonModules();
            this.isGuest = true;
        }
    }

    private async savePlayerInventory(): Promise<void> {
        try {
            const user = await apiClient.getMe();
            if (!user) return;

            await apiClient.saveInventory(this.inventory);
        } catch (error) {
            console.error('Failed to save inventory:', error);
        }
    }

    private async saveLoadout(): Promise<void> {
        try {
            const user = await apiClient.getMe();
            if (!user) return;

            const fittedModules = this.fittedModules.map(fm => ({
                slotIndex: fm.slotIndex,
                slotType: fm.slotType,
                module: fm.module ? {
                    id: fm.module.instanceId,
                    definitionId: fm.module.definitionId,
                    level: fm.module.level,
                } : null,
            }));
            await apiClient.saveLoadout(fittedModules);
        } catch (error) {
            console.error('Failed to save loadout:', error);
        }
    }

    update(time: number, delta: number): void {
        if (Math.abs(this.scrollVelocity) > this.MIN_VELOCITY && this.activeScrollGrid && !this.isPointerDown) {
            this.scrollGrid(this.activeScrollGrid, this.scrollVelocity);
            this.scrollVelocity *= this.SCROLL_FRICTION;
        } else if (Math.abs(this.scrollVelocity) <= this.MIN_VELOCITY) {
            this.scrollVelocity = 0;
        }
    }

    private initializeFittedSlots(): void {
        this.fittedModules = [
            { slotIndex: 0, slotType: 'weapon', module: null },
            { slotIndex: 1, slotType: 'weapon', module: null },
            { slotIndex: 2, slotType: 'support', module: null },
            { slotIndex: 3, slotType: 'support', module: null },
            { slotIndex: 4, slotType: 'support', module: null },
        ];
    }

    private get fonts() {
        return {
            huge: `${this.theme.fonts.sizeHuge}px`,
            large: `${this.theme.fonts.sizeLarge}px`,
            medium: `${this.theme.fonts.sizeMedium}px`,
            small: `${this.theme.fonts.sizeSmall}px`,
            label: `${this.theme.fonts.sizeLabel}px`,
        };
    }

    private get colors() {
        return this.theme.colors;
    }

    // ==================== UNIFIED INPUT HANDLING ====================

    private setupInput(): void {
        this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: any[], deltaX: number, deltaY: number) => {
            const grid = this.getGridAtPoint(pointer.x, pointer.y);
            if (grid) {
                this.scrollGrid(grid, deltaY * 0.5);
                this.activeScrollGrid = grid;
            }
        });

        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            this.isPointerDown = true;
            this.pointerDownTime = Date.now();
            this.pointerDownX = pointer.x;
            this.pointerDownY = pointer.y;
            this.lastPointerY = pointer.y;
            this.pointerMoveDistance = 0;
            this.scrollVelocity = 0;
            this.dragDisabled = false;  // Reset drag disabled flag

            this.pointerDownGrid = this.getGridAtPoint(pointer.x, pointer.y);
            if (this.pointerDownGrid) {
                this.activeScrollGrid = this.pointerDownGrid;
            }

            this.pointerDownTarget = this.findModuleCardAtPoint(pointer.x, pointer.y);
        });

        this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
            if (!this.isPointerDown) return;

            const dx = pointer.x - this.pointerDownX;
            const dy = pointer.y - this.pointerDownY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            this.pointerMoveDistance = distance;

            const holdTime = Date.now() - this.pointerDownTime;

            // If vertical movement exceeds tolerance during hold period, disable drag
            if (!this.isDragging && holdTime < this.DRAG_HOLD_TIME) {
                if (Math.abs(dy) > this.DRAG_VERTICAL_TOLERANCE) {
                    this.dragDisabled = true;
                }
            }

            // Only allow drag if:
            // 1. Held long enough
            // 2. Moved far enough
            // 3. Didn't have significant vertical movement during hold
            if (!this.isDragging && !this.dragDisabled && this.pointerDownTarget &&
                distance > this.DRAG_THRESHOLD && holdTime > this.DRAG_HOLD_TIME) {
                const playerModule = this.pointerDownTarget.getData('playerModule') as PlayerModule;
                if (playerModule && !this.isModuleFitted(playerModule)) {
                    this.startDragging(playerModule, pointer);
                }
            }

            if (this.isDragging && this.draggedModuleCard) {
                this.draggedModuleCard.setPosition(pointer.x, pointer.y);
                this.highlightValidSlots(this.draggedModule);
            } else if (this.pointerDownGrid && !this.isDragging) {
                const deltaY = this.lastPointerY - pointer.y;
                this.scrollGrid(this.pointerDownGrid, deltaY);
                this.scrollVelocity = deltaY;
            }

            this.lastPointerY = pointer.y;
        });

        this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
            if (this.isDragging) {
                this.endDragging(pointer);
            } else if (this.pointerMoveDistance < this.CLICK_THRESHOLD && this.pointerDownTarget) {
                const playerModule = this.pointerDownTarget.getData('playerModule') as PlayerModule;
                if (playerModule) {
                    this.selectModule(playerModule);
                }
            }

            this.isPointerDown = false;
            this.pointerDownTarget = null;
            this.pointerDownGrid = null;
        });
    }

    private getGridAtPoint(x: number, y: number): 'weapon' | 'support' | null {
        if (this.weaponGridBounds && this.weaponGridBounds.contains(x, y)) {
            return 'weapon';
        }
        if (this.supportGridBounds && this.supportGridBounds.contains(x, y)) {
            return 'support';
        }
        return null;
    }

    private findModuleCardAtPoint(x: number, y: number): Phaser.GameObjects.Container | null {
        // Only search the grid the pointer is actually in to prevent z-order issues
        const activeGrid = this.getGridAtPoint(x, y);

        if (activeGrid === 'weapon') {
            const weaponCards = this.weaponGridContainer.getAll() as Phaser.GameObjects.Container[];
            for (const card of weaponCards) {
                const worldX = this.weaponGridContainer.x + card.x;
                const worldY = this.weaponGridContainer.y + card.y;
                const halfSize = this.gridCellSize / 2;
                if (x >= worldX - halfSize && x <= worldX + halfSize &&
                    y >= worldY - halfSize && y <= worldY + halfSize) {
                    return card;
                }
            }
        } else if (activeGrid === 'support') {
            const supportCards = this.supportGridContainer.getAll() as Phaser.GameObjects.Container[];
            for (const card of supportCards) {
                const worldX = this.supportGridContainer.x + card.x;
                const worldY = this.supportGridContainer.y + card.y;
                const halfSize = this.gridCellSize / 2;
                if (x >= worldX - halfSize && x <= worldX + halfSize &&
                    y >= worldY - halfSize && y <= worldY + halfSize) {
                    return card;
                }
            }
        }

        return null;
    }

    private startDragging(module: PlayerModule, pointer: Phaser.Input.Pointer): void {
        this.isDragging = true;
        this.draggedModule = module;

        // Clear momentum when starting a drag
        this.scrollVelocity = 0;

        this.draggedModuleCard = this.moduleRenderer.createModuleCard(module, {
            size: this.gridCellSize,
            showLevel: true,
            showAffinities: true,
        });

        if (this.draggedModuleCard) {
            this.draggedModuleCard.setPosition(pointer.x, pointer.y);
            this.draggedModuleCard.setAlpha(0.9);
            this.draggedModuleCard.setDepth(1000);
        }

        if (this.pointerDownTarget) {
            this.pointerDownTarget.setAlpha(0.3);
        }
    }

    private endDragging(pointer: Phaser.Input.Pointer): void {
        if (this.draggedModule) {
            const targetSlot = this.getSlotAtPosition(pointer.x, pointer.y);
            const definition = getModuleDefinition(this.draggedModule.definitionId);

            if (targetSlot && definition && definition.slotType === targetSlot.slotType) {
                if (targetSlot.module === null) {
                    // Drop on empty slot - fit the module
                    if (this.draggedFromSlot) {
                        // Moving from one slot to another
                        this.draggedFromSlot.module = null;
                    }
                    targetSlot.module = this.draggedModule;
                } else if (targetSlot !== this.draggedFromSlot) {
                    // Drop on occupied slot - swap modules
                    const existingModule = targetSlot.module;
                    if (this.draggedFromSlot) {
                        // Swap between two fitted slots
                        this.draggedFromSlot.module = existingModule;
                        targetSlot.module = this.draggedModule;
                    } else {
                        // Swap inventory module with fitted module
                        targetSlot.module = this.draggedModule;
                        // existingModule goes back to inventory (already there, just unfitted)
                    }
                }
                this.updateWeaponGrid();
                this.updateSupportGrid();
                this.updateFittingSlots();
                this.updateShipVisual();
                this.saveLoadout();
            } else if (this.draggedFromSlot) {
                // Dragged from slot but dropped elsewhere - unfit the module
                this.draggedFromSlot.module = null;
                this.updateWeaponGrid();
                this.updateSupportGrid();
                this.updateFittingSlots();
                this.updateShipVisual();
                this.saveLoadout();
            }
            // If dragging from inventory and dropped nowhere valid, nothing happens (module stays in inventory)
        }

        if (this.draggedModuleCard) {
            this.draggedModuleCard.destroy();
            this.draggedModuleCard = null;
        }

        if (this.pointerDownTarget) {
            const pm = this.pointerDownTarget.getData('playerModule') as PlayerModule;
            if (pm) {
                this.pointerDownTarget.setAlpha(this.isModuleFitted(pm) ? 0.4 : 1);
            }
        }

        this.isDragging = false;
        this.draggedModule = null;
        this.draggedFromSlot = null;
        this.clearSlotHighlights();
    }

    private highlightValidSlots(module: PlayerModule | null): void {
        if (!module) return;
        const definition = getModuleDefinition(module.definitionId);
        if (!definition) return;

        this.slotContainers.forEach(slot => {
            const slotType = slot.getData('slotType');
            const fittedModule = slot.getData('fittedModule') as FittedModule;
            // Highlight any slot of matching type (empty or occupied for swap)
            // But not the slot we're dragging from
            if (slotType === definition.slotType && fittedModule !== this.draggedFromSlot) {
                slot.setScale(1.1);
            }
        });
    }

    private clearSlotHighlights(): void {
        this.slotContainers.forEach(slot => {
            slot.setScale(1);
        });
    }

    private getSlotAtPosition(x: number, y: number): FittedModule | null {
        // Calculate slot positions manually since getBounds can be unreliable with nested containers
        const slotSize = this.gridCellSize;
        const halfSize = slotSize / 2;

        for (let i = 0; i < this.slotContainers.length; i++) {
            const slot = this.slotContainers[i];
            // Get world position of slot
            const worldX = this.slotsContainer.x + slot.x;
            const worldY = this.slotsContainer.y + slot.y;

            if (x >= worldX - halfSize && x <= worldX + halfSize &&
                y >= worldY - halfSize && y <= worldY + halfSize) {
                return this.fittedModules[i];
            }
        }
        return null;
    }

    // ==================== LEFT PANEL ====================

    private createLeftPanel(): void {
        const panelX = 8;
        const panelY = 8;
        const panelWidth = this.LEFT_WIDTH - 16;
        const panelHeight = GAME_CONFIG.HEIGHT - 16;

        const panelGraphics = this.add.graphics();
        this.ui.drawBeveledPanel(panelGraphics, panelX, panelY, panelWidth, panelHeight, 'MODULES');

        const contentY = panelY + 50;
        const sortButtonHeight = 44;
        const contentHeight = panelHeight - 60 - sortButtonHeight - 10;  // Reserve space for sort buttons
        const selectedAreaHeight = contentHeight * 0.26;  // Slightly reduced to bring grids closer

        this.selectedModuleContainer = this.add.container(
            panelX + panelWidth / 2,
            contentY + selectedAreaHeight / 2
        );

        const gridsAreaHeight = contentHeight * 0.74;  // Adjusted to match reduced selection area
        const gridsStartY = contentY + selectedAreaHeight;
        const titleBarHeight = 36;
        const gridHeight = (gridsAreaHeight - titleBarHeight * 2 - 10) / 2;
        this.gridVisibleHeight = gridHeight;

        // WEAPONS
        const weaponTitleY = gridsStartY;
        const weaponTitleBg = this.add.graphics();
        weaponTitleBg.fillStyle(0x1a1a2e, 0.8);
        weaponTitleBg.fillRect(panelX + 10, weaponTitleY, panelWidth - 20, titleBarHeight);

        const weaponLabel = this.add.text(panelX + panelWidth / 2, weaponTitleY + titleBarHeight / 2, 'WEAPONS', {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.weaponColorHex,
            fontStyle: 'bold',
        });
        weaponLabel.setOrigin(0.5);

        const weaponGridY = weaponTitleY + titleBarHeight;
        this.weaponGridBaseY = weaponGridY;
        this.weaponGridContainer = this.add.container(panelX + 16, weaponGridY);

        this.weaponGridBounds = new Phaser.Geom.Rectangle(
            panelX + 8, weaponGridY, panelWidth - 16, this.gridVisibleHeight
        );

        const weaponMaskShape = this.make.graphics({});
        weaponMaskShape.fillStyle(0xffffff);
        weaponMaskShape.fillRect(panelX + 8, weaponGridY, panelWidth - 16, this.gridVisibleHeight);
        this.weaponGridContainer.setMask(weaponMaskShape.createGeometryMask());

        this.weaponGradientTop = this.add.graphics();
        this.weaponGradientBottom = this.add.graphics();
        this.drawScrollGradient(this.weaponGradientTop, panelX + 8, weaponGridY, panelWidth - 16, 30, 'top');
        this.drawScrollGradient(this.weaponGradientBottom, panelX + 8, weaponGridY + this.gridVisibleHeight - 30, panelWidth - 16, 30, 'bottom');
        this.weaponGradientTop.setAlpha(0);
        this.weaponGradientBottom.setAlpha(0);

        // SUPPORT
        const supportTitleY = weaponGridY + this.gridVisibleHeight + 5;
        const supportTitleBg = this.add.graphics();
        supportTitleBg.fillStyle(0x1a1a2e, 0.8);
        supportTitleBg.fillRect(panelX + 10, supportTitleY, panelWidth - 20, titleBarHeight);

        const supportLabel = this.add.text(panelX + panelWidth / 2, supportTitleY + titleBarHeight / 2, 'SUPPORT', {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.supportColorHex,
            fontStyle: 'bold',
        });
        supportLabel.setOrigin(0.5);

        const supportGridY = supportTitleY + titleBarHeight;
        this.supportGridBaseY = supportGridY;
        this.supportGridContainer = this.add.container(panelX + 16, supportGridY);

        this.supportGridBounds = new Phaser.Geom.Rectangle(
            panelX + 8, supportGridY, panelWidth - 16, this.gridVisibleHeight
        );

        const supportMaskShape = this.make.graphics({});
        supportMaskShape.fillStyle(0xffffff);
        supportMaskShape.fillRect(panelX + 8, supportGridY, panelWidth - 16, this.gridVisibleHeight);
        this.supportGridContainer.setMask(supportMaskShape.createGeometryMask());

        this.supportGradientTop = this.add.graphics();
        this.supportGradientBottom = this.add.graphics();
        this.drawScrollGradient(this.supportGradientTop, panelX + 8, supportGridY, panelWidth - 16, 30, 'top');
        this.drawScrollGradient(this.supportGradientBottom, panelX + 8, supportGridY + this.gridVisibleHeight - 30, panelWidth - 16, 30, 'bottom');
        this.supportGradientTop.setAlpha(0);
        this.supportGradientBottom.setAlpha(0);

        // SORT BUTTONS at bottom
        const sortY = panelY + panelHeight - sortButtonHeight - 10;
        this.sortButtonsContainer = this.add.container(panelX + panelWidth / 2, sortY + sortButtonHeight / 2);
        this.createSortButtons(panelWidth - 20, sortButtonHeight);
    }

    private createSortButtons(totalWidth: number, height: number): void {
        const sortOptions: Array<{ key: 'rarity' | 'color' | 'level' | 'type'; label: string }> = [
            { key: 'rarity', label: 'RARITY' },
            { key: 'color', label: 'COLOR' },
            { key: 'level', label: 'LEVEL' },
            { key: 'type', label: 'TYPE' },
        ];

        const buttonWidth = totalWidth / 4;

        sortOptions.forEach((opt, i) => {
            const x = -totalWidth / 2 + buttonWidth * i + buttonWidth / 2;
            const isActive = this.currentSort === opt.key;
            const arrow = isActive ? (this.sortReversed ? ' ▲' : ' ▼') : '';

            const bg = this.add.graphics();
            bg.fillStyle(isActive ? 0x3a3a5e : 0x1a1a2e, 1);
            bg.fillRect(-buttonWidth / 2 + 2, -height / 2 + 2, buttonWidth - 4, height - 4);
            if (isActive) {
                bg.lineStyle(2, 0x5a5a8e, 1);
                bg.strokeRect(-buttonWidth / 2 + 2, -height / 2 + 2, buttonWidth - 4, height - 4);
            }

            const label = this.add.text(0, 0, opt.label + arrow, {
                fontFamily: 'monospace',
                fontSize: this.fonts.small,
                color: isActive ? '#ffffff' : '#888888',
                fontStyle: isActive ? 'bold' : 'normal',
            });
            label.setOrigin(0.5);

            const container = this.add.container(x, 0, [bg, label]);
            container.setSize(buttonWidth - 4, height - 4);
            container.setInteractive({ useHandCursor: true });

            container.on('pointerdown', () => {
                if (this.currentSort === opt.key) {
                    // Same button - toggle reverse
                    this.sortReversed = !this.sortReversed;
                } else {
                    // Different button - reset reverse
                    this.currentSort = opt.key;
                    this.sortReversed = false;
                }
                this.updateSortButtons();
                this.updateWeaponGrid();
                this.updateSupportGrid();
            });

            this.sortButtonsContainer.add(container);
        });
    }

    private updateSortButtons(): void {
        this.sortButtonsContainer.removeAll(true);
        const panelWidth = this.LEFT_WIDTH - 16;
        this.createSortButtons(panelWidth - 20, 44);
    }

    private updateSelectedModuleDisplay(): void {
        this.selectedModuleContainer.removeAll(true);

        const panelWidth = this.LEFT_WIDTH - 48;
        const sortButtonHeight = 44;
        const contentHeight = (GAME_CONFIG.HEIGHT - 16) - 60 - sortButtonHeight - 10;
        const areaHeight = contentHeight * 0.26 - 10;

        const bg = this.add.graphics();
        bg.fillStyle(0x0a0a12, 1);
        bg.fillRoundedRect(-panelWidth / 2, -areaHeight / 2, panelWidth, areaHeight, 4);
        this.selectedModuleContainer.add(bg);

        if (!this.selectedModule) {
            const placeholder = this.add.text(0, 0, 'SELECT A MODULE', {
                fontFamily: 'monospace',
                fontSize: this.fonts.small,
                color: this.colors.textMuted,
            });
            placeholder.setOrigin(0.5);
            this.selectedModuleContainer.add(placeholder);
            return;
        }

        const definition = getModuleDefinition(this.selectedModule.definitionId);
        if (!definition) return;

        // Layout: Left column [Card + Upgrade + Scrap stacked], Right column [Stats + Description]
        const buttonWidth = 145;
        const buttonHeight = 40;
        const buttonGap = 5;
        const cardSize = buttonWidth;  // Match card width to button width
        const leftColumnWidth = cardSize + 20;
        const leftColumnX = -panelWidth / 2 + leftColumnWidth / 2 + 10;
        const rightColumnX = -panelWidth / 2 + leftColumnWidth + 15;
        const rightColumnWidth = panelWidth - leftColumnWidth - 30;

        // Module card preview (moved up, closer to top)
        const cardY = -areaHeight / 2 + cardSize / 2 + 8;
        const card = this.moduleRenderer.createModuleCard(this.selectedModule, {
            size: cardSize,
            showLevel: true,
            showAffinities: true,
        });
        if (card) {
            card.setPosition(leftColumnX, cardY);
            this.selectedModuleContainer.add(card);
        }

        // Upgrade/Evolve button (right below card)
        const isMaxLevel = this.selectedModule.level >= 10;
        const evolutionRecipe = getEvolutionRecipe(this.selectedModule.definitionId);
        const hasEvolutionRecipe = evolutionRecipe !== undefined;
        const upgradeCost = definition.cmpCost;
        const canAffordUpgrade = this.playerScrap >= upgradeCost;

        // Evolution requires: blueprints based on target rarity + cmpCost in scrap
        // Evolving to rare = 1 BP, to officer = 2 BP
        const evolvedDef = evolutionRecipe ? getModuleDefinition(evolutionRecipe.toModuleId) : null;
        const blueprintsNeeded = evolvedDef?.rarity === 'officer' ? 2 : 1;
        const availableBlueprints = definition.slotType === 'weapon' ? this.weaponBlueprints : this.supportBlueprints;
        const hasBlueprint = availableBlueprints >= blueprintsNeeded;
        const canAffordEvolve = this.playerScrap >= upgradeCost && hasBlueprint;
        const canEvolve = isMaxLevel && hasEvolutionRecipe && canAffordEvolve;

        let upgradeText: string;
        let upgradeColor: number;
        let upgradeEnabled: boolean;

        if (!isMaxLevel) {
            upgradeText = `UPGRADE ${upgradeCost}`;
            upgradeColor = canAffordUpgrade ? 0x2ecc71 : 0x444455;
            upgradeEnabled = canAffordUpgrade;
        } else if (hasEvolutionRecipe) {
            // Show what's needed for evolution
            if (!hasBlueprint) {
                upgradeText = `NEED ${blueprintsNeeded} BP`;
                upgradeColor = 0x444455;
                upgradeEnabled = false;
            } else if (this.playerScrap < upgradeCost) {
                upgradeText = `EVOLVE ${upgradeCost}`;
                upgradeColor = 0x444455;
                upgradeEnabled = false;
            } else {
                upgradeText = `EVOLVE ${upgradeCost}`;
                upgradeColor = 0x9b59b6;
                upgradeEnabled = true;
            }
        } else {
            upgradeText = 'MAX LEVEL';
            upgradeColor = 0x444455;
            upgradeEnabled = false;
        }

        const upgradeBtnY = cardY + cardSize / 2 + buttonHeight / 2 + buttonGap;
        const upgradeBtnBg = this.add.graphics();
        upgradeBtnBg.fillStyle(upgradeColor, 1);
        upgradeBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);

        const upgradeBtnLabel = this.add.text(0, 0, upgradeText, {
            fontFamily: 'monospace',
            fontSize: '20px',
            color: upgradeEnabled ? '#ffffff' : '#999999',
            fontStyle: 'bold',
        });
        upgradeBtnLabel.setOrigin(0.5);

        const upgradeBtnContainer = this.add.container(leftColumnX, upgradeBtnY, [upgradeBtnBg, upgradeBtnLabel]);
        upgradeBtnContainer.setSize(buttonWidth, buttonHeight);

        if (upgradeEnabled) {
            upgradeBtnContainer.setInteractive({ useHandCursor: true });
            upgradeBtnContainer.on('pointerover', () => {
                upgradeBtnBg.clear();
                upgradeBtnBg.fillStyle(upgradeColor, 0.7);
                upgradeBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);
            });
            upgradeBtnContainer.on('pointerout', () => {
                upgradeBtnBg.clear();
                upgradeBtnBg.fillStyle(upgradeColor, 1);
                upgradeBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);
            });
            upgradeBtnContainer.on('pointerdown', () => {
                if (canEvolve) {
                    this.handleEvolve(this.selectedModule!);
                } else {
                    this.handleUpgrade(this.selectedModule!);
                }
            });
        }
        this.selectedModuleContainer.add(upgradeBtnContainer);

        // Scrap button (below upgrade button)
        const scrapValue = definition.cmpCost * (this.selectedModule.level - 1);
        const isFitted = this.isModuleFitted(this.selectedModule);
        const scrapEnabled = !isFitted;
        const scrapColor = scrapEnabled ? 0xe67e22 : 0x444455;

        const scrapBtnY = upgradeBtnY + buttonHeight + buttonGap;
        const scrapBtnBg = this.add.graphics();
        scrapBtnBg.fillStyle(scrapColor, 1);
        scrapBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);

        const scrapBtnLabel = this.add.text(0, 0, `SCRAP +${scrapValue}`, {
            fontFamily: 'monospace',
            fontSize: '20px',
            color: scrapEnabled ? '#ffffff' : '#999999',
            fontStyle: 'bold',
        });
        scrapBtnLabel.setOrigin(0.5);

        const scrapBtnContainer = this.add.container(leftColumnX, scrapBtnY, [scrapBtnBg, scrapBtnLabel]);
        scrapBtnContainer.setSize(buttonWidth, buttonHeight);

        if (scrapEnabled) {
            scrapBtnContainer.setInteractive({ useHandCursor: true });
            scrapBtnContainer.on('pointerover', () => {
                scrapBtnBg.clear();
                scrapBtnBg.fillStyle(scrapColor, 0.7);
                scrapBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);
            });
            scrapBtnContainer.on('pointerout', () => {
                scrapBtnBg.clear();
                scrapBtnBg.fillStyle(scrapColor, 1);
                scrapBtnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 6);
            });
            scrapBtnContainer.on('pointerdown', () => {
                this.handleScrap(this.selectedModule!);
            });
        }
        this.selectedModuleContainer.add(scrapBtnContainer);

        // Right column: Stats at top (moved up to align better with card)
        const name = this.add.text(rightColumnX, -areaHeight / 2 + 10, definition.name.toUpperCase(), {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textPrimary,
            fontStyle: 'bold',
        });
        this.selectedModuleContainer.add(name);

        const levelText = this.selectedModule.level >= 10 ? 'MAX' : `LVL ${this.selectedModule.level}`;
        const meta = this.add.text(rightColumnX, -areaHeight / 2 + 34,
            `${definition.slotType.toUpperCase()} • ${definition.rarity.toUpperCase()} • ${levelText}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.selectedModule.level >= 10 ? this.colors.textAccent : this.colors.textSecondary,
        });
        this.selectedModuleContainer.add(meta);

        const costs = this.add.text(rightColumnX, -areaHeight / 2 + 58,
            `CMP: ${definition.cmpCost}  RCT: ${definition.rctCost}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textSecondary,
        });
        this.selectedModuleContainer.add(costs);

        // Separator line
        const separator = this.add.graphics();
        separator.lineStyle(1, 0x3a3a5e, 0.6);
        separator.lineBetween(rightColumnX, -areaHeight / 2 + 80, rightColumnX + rightColumnWidth - 10, -areaHeight / 2 + 80);
        this.selectedModuleContainer.add(separator);

        // Description (below separator, with more width)
        const description = this.generateModuleDescription(definition, this.selectedModule);
        const descText = this.add.text(rightColumnX, -areaHeight / 2 + 88, description, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textSecondary,
            wordWrap: { width: rightColumnWidth - 10, useAdvancedWrap: true },
            lineSpacing: 4,
        });
        this.selectedModuleContainer.add(descText);
    }

    private generateModuleDescription(definition: ModuleDefinition, playerModule: PlayerModule): string {
        const lines: string[] = [];
        const level = playerModule.level;
        const levelBonus = level - 1;
        const scaling = definition.scaling as Record<string, number> | undefined;

        // Use instance aspects for damage type and charge sources
        const moduleAspects = playerModule.aspects || [];
        const aspectToOrb: Record<string, string> = {
            THERMAL: 'R', CRYO: 'B', RADIATION: 'G', EMP: 'Y', VOID: 'P',
        };

        // Weapon damage info
        if (definition.slotType === 'weapon' && definition.scaling) {
            const baseDmg = definition.scaling.baseDamage;
            const perLevel = definition.scaling.damagePerLevel || 0;
            const totalDmg = Math.round(baseDmg + perLevel * levelBonus);
            const colorNames: Record<string, string> = { R: 'Red', B: 'Blue', G: 'Green', Y: 'Yellow', P: 'Purple' };
            // Charge sources come from module's aspects
            const orbColors = moduleAspects.map(a => aspectToOrb[a] || 'R');
            const colorList = orbColors.map(c => colorNames[c] || c).join('/') || '';
            const dmgType = moduleAspects[0] || '';
            lines.push(`Deals ${totalDmg} ${dmgType} damage.`);
            if (colorList) lines.push(`Charges from ${colorList} orbs.`);
        }

        // Passive effects
        if (definition.passiveEffects && definition.slotType === 'support') {
            for (const eff of definition.passiveEffects) {
                const p = eff.params as Record<string, unknown>;
                if (eff.effectId === 'eff_shield_gain_multiplier') {
                    const baseMult = (p.multiplierAdd as number) || 0;
                    const perLevel = scaling?.multiplierPerLevel || 0;
                    const totalMult = baseMult + perLevel * levelBonus;
                    const pct = (totalMult * 100).toFixed(0);
                    lines.push(`+${pct}% shield gain.`);
                } else if (eff.effectId === 'eff_add_shield_max') {
                    const baseAdd = (p.add as number) || 0;
                    const perLevel = scaling?.addPerLevel || 0;
                    const totalAdd = Math.round(baseAdd + perLevel * levelBonus);
                    lines.push(`+${totalAdd} max shield.`);
                } else if (eff.effectId === 'eff_add_resist') {
                    const baseResist = (p.resistAdd as number) || 0;
                    const perLevel = scaling?.resistPerLevel || 0;
                    const totalResist = baseResist + perLevel * levelBonus;
                    const pct = (totalResist * 100).toFixed(0);
                    lines.push(`+${pct}% ${p.damageType} resist.`);
                } else if (eff.effectId === 'eff_charge_bonus_on_match') {
                    const colorNames: Record<string, string> = { R: 'Red', B: 'Blue', G: 'Green', Y: 'Yellow', P: 'Purple' };
                    const baseBonus = (p.bonusPerOrb as number) || 0;
                    const perLevel = scaling?.bonusPerLevel || 0;
                    const totalBonus = baseBonus + perLevel * levelBonus;
                    lines.push(`+${totalBonus.toFixed(1)} charge from ${colorNames[p.color as string] || p.color} orbs.`);
                } else if (eff.effectId === 'eff_add_fit_budget') {
                    const baseCmp = (p.cmpAdd as number) || 0;
                    const baseRct = (p.rctAdd as number) || 0;
                    const perLevel = scaling?.budgetPerLevel || 0;
                    const totalCmp = Math.round(baseCmp + perLevel * levelBonus);
                    const totalRct = Math.round(baseRct + perLevel * levelBonus);
                    if (totalCmp && totalRct && totalCmp === totalRct) {
                        lines.push(`+${totalCmp} CMP/RCT budget.`);
                    } else {
                        if (totalCmp) lines.push(`+${totalCmp} CMP budget.`);
                        if (totalRct) lines.push(`+${totalRct} RCT budget.`);
                    }
                } else if (eff.effectId === 'eff_weapon_damage_multiplier') {
                    const baseMult = (p.multiplierAdd as number) || 0;
                    const perLevel = scaling?.damageMultPerLevel || 0;
                    const totalMult = baseMult + perLevel * levelBonus;
                    const pct = (totalMult * 100).toFixed(0);
                    const weaponType = (p.weaponType as string) || 'weapon';
                    const displayType = weaponType.charAt(0).toUpperCase() + weaponType.slice(1);
                    lines.push(`+${pct}% ${displayType} damage.`);
                } else if (eff.effectId === 'eff_weapon_charge_cost_multiplier') {
                    const baseMult = (p.multiplierAdd as number) || 0;
                    const perLevel = scaling?.costMultPerLevel || 0;
                    const totalMult = baseMult + perLevel * levelBonus;
                    const pct = (totalMult * -100).toFixed(0);
                    const weaponType = (p.weaponType as string) || 'weapon';
                    const displayType = weaponType.charAt(0).toUpperCase() + weaponType.slice(1);
                    lines.push(`-${pct}% ${displayType} charge cost.`);
                }
            }
        }

        // Active skill
        if (definition.skill) {
            const colorNames: Record<string, string> = { R: 'Red', B: 'Blue', G: 'Green', Y: 'Yellow', P: 'Purple' };
            // Skill charge sources come from module's rolled aspects
            const orbColors = moduleAspects.map(a => aspectToOrb[a] || 'R');
            const colorList = orbColors.map(c => colorNames[c] || c).join('/');
            // Apply skill charge cost scaling
            const baseChargeCost = definition.skill.chargeCost || 10;
            const chargeCostPerLevel = scaling?.chargeCostPerLevel || 0;
            const totalChargeCost = Math.max(1, Math.round(baseChargeCost + chargeCostPerLevel * levelBonus));
            lines.push(`SKILL (${totalChargeCost} ${colorList}):`);

            for (const eff of definition.skill.castEffects) {
                const p = eff.params as Record<string, unknown>;
                if (eff.effectId === 'eff_add_move_time') {
                    lines.push(`+${p.seconds}s move time for ${p.durationTurns} turns.`);
                } else if (eff.effectId === 'eff_skill_convert_random') {
                    lines.push(`Convert ${p.count} orbs to Shield.`);
                } else if (eff.effectId === 'eff_skill_loot_boost') {
                    lines.push(`Better loot for ${p.durationNodes} nodes.`);
                }
            }
        }

        return lines.slice(0, 3).join(' ');  // Limit to avoid overflow
    }

    private async handleUpgrade(module: PlayerModule): Promise<void> {
        const definition = getModuleDefinition(module.definitionId);
        if (!definition) return;

        const cost = definition.cmpCost;
        if (module.level >= 10 || this.playerScrap < cost) return;

        // Deduct scrap locally
        this.playerScrap -= cost;
        module.level++;

        // Save to server
        try {
            await apiClient.updateScrap(-cost);
            await this.savePlayerInventory();
        } catch (error) {
            console.error('Failed to save upgrade:', error);
        }

        this.updateScrapDisplay();
        this.updateSelectedModuleDisplay();
        this.updateWeaponGrid();
        this.updateSupportGrid();
        this.updateFittingSlots();
        this.updateShipVisual();
    }

    private async handleScrap(module: PlayerModule): Promise<void> {
        const definition = getModuleDefinition(module.definitionId);
        if (!definition) return;

        // Can't scrap a fitted module
        if (this.isModuleFitted(module)) return;

        const scrapValue = definition.cmpCost * (module.level - 1);

        // Add scrap locally
        this.playerScrap += scrapValue;

        // Remove module from inventory
        const index = this.inventory.findIndex(m => m.instanceId === module.instanceId);
        if (index !== -1) {
            this.inventory.splice(index, 1);
        }

        // Save to server
        try {
            await apiClient.updateScrap(scrapValue);
            await this.savePlayerInventory();
        } catch (error) {
            console.error('Failed to save scrap:', error);
        }

        // Clear selection and update UI
        this.selectedModule = null;
        this.updateScrapDisplay();
        this.updateSelectedModuleDisplay();
        this.updateWeaponGrid();
        this.updateSupportGrid();
    }

    private async handleEvolve(module: PlayerModule): Promise<void> {
        const recipe = getEvolutionRecipe(module.definitionId);
        if (!recipe) return;

        const definition = getModuleDefinition(module.definitionId);
        if (!definition) return;

        const evolveCost = definition.cmpCost;
        const isWeapon = definition.slotType === 'weapon';

        // Blueprint cost based on target rarity: rare = 1, officer = 2
        const evolvedDef = getModuleDefinition(recipe.toModuleId);
        const blueprintsNeeded = evolvedDef?.rarity === 'officer' ? 2 : 1;
        const availableBlueprints = isWeapon ? this.weaponBlueprints : this.supportBlueprints;
        const hasBlueprint = availableBlueprints >= blueprintsNeeded;

        if (!hasBlueprint || this.playerScrap < evolveCost) {
            return; // Button should be disabled, but double-check
        }

        // Deduct costs locally
        this.playerScrap -= evolveCost;
        if (isWeapon) {
            this.weaponBlueprints -= blueprintsNeeded;
        } else {
            this.supportBlueprints -= blueprintsNeeded;
        }

        // Replace module in inventory with evolved version
        const moduleIndex = this.inventory.findIndex(m => m.instanceId === module.instanceId);
        let evolvedModule: PlayerModule | null = null;
        if (moduleIndex !== -1) {
            // Preserve existing aspects and add new ones if needed for officer rarity
            let newAspects = [...(module.aspects || [])];
            if (evolvedDef?.rarity === 'officer' && newAspects.length < 2) {
                // Officer needs 2 aspects, add one if we only have 1
                newAspects = rollAdditionalAspect(newAspects);
            }

            // Create evolved module with same instance ID pattern but new definition
            evolvedModule = {
                definitionId: recipe.toModuleId,
                instanceId: `${recipe.toModuleId}_${Date.now()}`, // New unique ID
                level: 1, // Evolved modules start at level 1
                aspects: newAspects,
            };
            this.inventory[moduleIndex] = evolvedModule;

            // If this module was fitted, update the fitted slot reference
            for (const slot of this.fittedModules) {
                if (slot.module?.instanceId === module.instanceId) {
                    slot.module = evolvedModule;
                    break;
                }
            }
        }

        // Save to server
        try {
            await apiClient.updateScrap(-evolveCost);
            if (isWeapon) {
                await apiClient.updateBlueprints(-blueprintsNeeded, 0);
            } else {
                await apiClient.updateBlueprints(0, -blueprintsNeeded);
            }
            await this.savePlayerInventory();
        } catch (error) {
            console.error('Failed to save evolution:', error);
        }

        // Show evolution success message
        const evolvedModuleDef = getModuleDefinition(recipe.toModuleId);
        const evolvedName = evolvedModuleDef?.name || 'Unknown Module';

        const notice = this.add.text(
            GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2,
            `EVOLVED TO: ${evolvedName}`,
            { fontFamily: 'monospace', fontSize: this.fonts.medium, color: '#9b59b6', fontStyle: 'bold' }
        );
        notice.setOrigin(0.5);

        this.tweens.add({
            targets: notice, alpha: 0, duration: 2500, ease: 'Cubic.easeIn',
            onComplete: () => {
                notice.destroy();
            },
        });

        // Update UI - select the evolved module if we have it
        this.selectedModule = evolvedModule;
        this.updateScrapDisplay();
        this.updateSelectedModuleDisplay();
        this.updateWeaponGrid();
        this.updateSupportGrid();
        this.updateFittingSlots();
        this.updateShipVisual();
        this.saveLoadout();
    }

    private drawScrollGradient(
        graphics: Phaser.GameObjects.Graphics,
        x: number, y: number, width: number, height: number, direction: 'top' | 'bottom'
    ): void {
        graphics.clear();
        const steps = 10;
        for (let i = 0; i < steps; i++) {
            const alpha = direction === 'top' ? (1 - i / steps) * 0.9 : (i / steps) * 0.9;
            graphics.fillStyle(this.colors.panelBg, alpha);
            graphics.fillRect(x, y + (i * height / steps), width, height / steps + 1);
        }
        graphics.setDepth(100);
    }

    private scrollGrid(gridType: 'weapon' | 'support', deltaY: number): void {
        if (gridType === 'weapon') {
            this.weaponScrollY = Phaser.Math.Clamp(this.weaponScrollY + deltaY, 0, this.weaponMaxScrollY);
            this.weaponGridContainer.y = this.weaponGridBaseY - this.weaponScrollY;
            this.updateWeaponGradients();
        } else {
            this.supportScrollY = Phaser.Math.Clamp(this.supportScrollY + deltaY, 0, this.supportMaxScrollY);
            this.supportGridContainer.y = this.supportGridBaseY - this.supportScrollY;
            this.updateSupportGradients();
        }
    }

    private updateWeaponGradients(): void {
        this.weaponGradientTop.setAlpha(this.weaponScrollY > 0 ? 1 : 0);
        this.weaponGradientBottom.setAlpha(this.weaponScrollY < this.weaponMaxScrollY ? 1 : 0);
    }

    private updateSupportGradients(): void {
        this.supportGradientTop.setAlpha(this.supportScrollY > 0 ? 1 : 0);
        this.supportGradientBottom.setAlpha(this.supportScrollY < this.supportMaxScrollY ? 1 : 0);
    }

    private sortModules(modules: PlayerModule[]): PlayerModule[] {
        const colorOrder: Record<string, number> = { R: 0, B: 1, G: 2, Y: 3, P: 4 };
        const rarityOrder: Record<string, number> = { officer: 0, rare: 1, common: 2 };

        const sorted = [...modules].sort((a, b) => {
            const defA = getModuleDefinition(a.definitionId);
            const defB = getModuleDefinition(b.definitionId);
            if (!defA || !defB) return 0;

            switch (this.currentSort) {
                case 'rarity':
                    if (defA.rarity !== defB.rarity) return rarityOrder[defA.rarity] - rarityOrder[defB.rarity];
                    return b.level - a.level;

                case 'color': {
                    // Get orb colors from module instance's rolled aspects
                    const aspectToOrb: Record<string, string> = {
                        THERMAL: 'R', CRYO: 'B', RADIATION: 'G', EMP: 'Y', VOID: 'P',
                    };
                    const colorsA = (a.aspects || []).map(asp => aspectToOrb[asp] || 'R');
                    const colorsB = (b.aspects || []).map(asp => aspectToOrb[asp] || 'R');
                    const primaryA = colorsA[0] || 'Z';
                    const primaryB = colorsB[0] || 'Z';
                    const primaryOrderA = colorOrder[primaryA] ?? 99;
                    const primaryOrderB = colorOrder[primaryB] ?? 99;
                    if (primaryOrderA !== primaryOrderB) return primaryOrderA - primaryOrderB;
                    // Secondary sort within same primary
                    const secondaryA = colorsA[1] || 'Z';
                    const secondaryB = colorsB[1] || 'Z';
                    const secOrderA = colorOrder[secondaryA] ?? 99;
                    const secOrderB = colorOrder[secondaryB] ?? 99;
                    if (secOrderA !== secOrderB) return secOrderA - secOrderB;
                    return b.level - a.level;
                }

                case 'level':
                    if (a.level !== b.level) return b.level - a.level;
                    return rarityOrder[defA.rarity] - rarityOrder[defB.rarity];

                case 'type':
                    // Sort by module name/type
                    if (defA.name !== defB.name) return defA.name.localeCompare(defB.name);
                    return b.level - a.level;

                default:
                    return 0;
            }
        });

        return this.sortReversed ? sorted.reverse() : sorted;
    }

    private updateWeaponGrid(): void {
        this.weaponGridContainer.removeAll(true);

        const weapons = this.sortModules(
            this.inventory.filter(m => getModuleDefinition(m.definitionId)?.slotType === 'weapon')
        );

        this.createModuleGrid(this.weaponGridContainer, weapons);

        const totalRows = Math.ceil(weapons.length / this.GRID_COLS);
        const gridHeight = totalRows * (this.gridCellSize + this.gridCellGap);
        this.weaponMaxScrollY = Math.max(0, gridHeight - this.gridVisibleHeight);
        this.updateWeaponGradients();
    }

    private updateSupportGrid(): void {
        this.supportGridContainer.removeAll(true);

        const supports = this.sortModules(
            this.inventory.filter(m => getModuleDefinition(m.definitionId)?.slotType === 'support')
        );

        this.createModuleGrid(this.supportGridContainer, supports);

        const totalRows = Math.ceil(supports.length / this.GRID_COLS);
        const gridHeight = totalRows * (this.gridCellSize + this.gridCellGap);
        this.supportMaxScrollY = Math.max(0, gridHeight - this.gridVisibleHeight);
        this.updateSupportGradients();
    }

    private createModuleGrid(container: Phaser.GameObjects.Container, modules: PlayerModule[]): void {
        const totalGridWidth = this.GRID_COLS * this.gridCellSize + (this.GRID_COLS - 1) * this.gridCellGap;
        const availableWidth = this.LEFT_WIDTH - 48;
        const offsetX = (availableWidth - totalGridWidth) / 2;

        modules.forEach((playerModule, index) => {
            const col = index % this.GRID_COLS;
            const row = Math.floor(index / this.GRID_COLS);
            const x = offsetX + col * (this.gridCellSize + this.gridCellGap) + this.gridCellSize / 2;
            const y = row * (this.gridCellSize + this.gridCellGap) + this.gridCellSize / 2;

            const isFitted = this.isModuleFitted(playerModule);
            const isSelected = this.selectedModule?.instanceId === playerModule.instanceId;

            const card = this.moduleRenderer.createModuleCard(playerModule, {
                size: this.gridCellSize,
                showLevel: true,
                showAffinities: true,
                isFitted,
                isSelected,
            });

            if (card) {
                card.setPosition(x, y);
                card.setData('playerModule', playerModule);
                if (isFitted) card.setAlpha(0.4);
                container.add(card);
            }
        });
    }

    private isModuleFitted(module: PlayerModule): boolean {
        return this.fittedModules.some(fm => fm.module?.instanceId === module.instanceId);
    }

    private selectModule(module: PlayerModule): void {
        if (this.selectedModule?.instanceId === module.instanceId) {
            this.selectedModule = null;
        } else {
            this.selectedModule = module;
        }
        this.updateSelectedModuleDisplay();
        this.updateWeaponGrid();
        this.updateSupportGrid();
    }

    // ==================== MIDDLE PANEL ====================

    private createMiddlePanel(): void {
        const panelX = this.MIDDLE_X + 8;
        const panelY = 8;
        const panelWidth = this.MIDDLE_WIDTH - 16;
        const panelHeight = GAME_CONFIG.HEIGHT - 16;

        const panelGraphics = this.add.graphics();
        this.ui.drawBeveledPanel(panelGraphics, panelX, panelY, panelWidth, panelHeight, 'SHIP FITTING');

        // Scrap display in upper left of panel (below title bar which ends at ~50px)
        this.createScrapDisplay(panelX + 20, panelY + 65);

        const shipAreaHeight = panelHeight * 0.42;
        const statsAreaHeight = panelHeight * 0.23;
        const fittingAreaHeight = panelHeight * 0.35 - 50;

        this.shipSprite = new ShipSprite(this,
            panelX + panelWidth / 2,
            panelY + 50 + shipAreaHeight / 2,
            { scale: 1.6 }
        );

        // Blueprint connection lines layer (drawn behind stats)
        this.connectionLines = this.add.graphics();

        this.statsContainer = this.add.container(
            panelX + panelWidth / 2,
            panelY + 50 + shipAreaHeight + statsAreaHeight / 2
        );

        this.slotsContainer = this.add.container(
            panelX + panelWidth / 2,
            panelY + 50 + shipAreaHeight + statsAreaHeight + fittingAreaHeight / 2
        );
    }

    private createScrapDisplay(x: number, y: number): void {
        // Scrap icon (stylized gear/currency)
        const icon = this.add.graphics();
        icon.fillStyle(0xf1c40f, 1);
        icon.fillCircle(x, y, 10);
        icon.fillStyle(0x1a1a2e, 1);
        icon.fillCircle(x, y, 4);

        // Scrap amount text (to the right of icon)
        this.scrapText = this.add.text(x + 15, y, `${this.playerScrap}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.medium,
            color: this.colors.textAccent,
            fontStyle: 'bold',
        });
        this.scrapText.setOrigin(0, 0.5);

        // Blueprint display below scrap
        const bpY = y + 28;

        // Blueprint icon (stylized scroll/blueprint)
        const bpIcon = this.add.graphics();
        bpIcon.fillStyle(0x9b59b6, 1);
        bpIcon.fillRoundedRect(x - 8, bpY - 8, 16, 16, 3);
        bpIcon.fillStyle(0x1a1a2e, 1);
        bpIcon.fillRect(x - 4, bpY - 4, 8, 2);
        bpIcon.fillRect(x - 4, bpY, 8, 2);
        bpIcon.fillRect(x - 4, bpY + 4, 5, 2);

        // Blueprint counts text
        this.blueprintText = this.add.text(x + 15, bpY,
            `W:${this.weaponBlueprints} S:${this.supportBlueprints}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: '#9b59b6',
            fontStyle: 'bold',
        });
        this.blueprintText.setOrigin(0, 0.5);
    }

    private updateScrapDisplay(): void {
        if (this.scrapText) {
            this.scrapText.setText(`${this.playerScrap}`);
        }
        if (this.blueprintText) {
            this.blueprintText.setText(`W:${this.weaponBlueprints} S:${this.supportBlueprints}`);
        }
    }

    private updateShipVisual(): void {
        this.shipSprite.resetColors();

        const weaponModules = this.fittedModules.filter(fm => fm.slotType === 'weapon' && fm.module);
        const supportModules = this.fittedModules.filter(fm => fm.slotType === 'support' && fm.module);

        if (weaponModules.length > 0) {
            const firstWeapon = weaponModules[0].module;
            if (firstWeapon && firstWeapon.aspects && firstWeapon.aspects.length > 0) {
                const aspectColors: Record<string, number> = {
                    THERMAL: 0x4a2020, CRYO: 0x203a4a, RADIATION: 0x204a20, EMP: 0x4a4a20, VOID: 0x3a204a,
                };
                const color = aspectColors[firstWeapon.aspects[0]] || 0x2a2a40;
                this.shipSprite.setRegionTint('wings', color);
                this.shipSprite.setRegionTint('engines', color);
            }
        }

        if (supportModules.length > 0) {
            const firstSupport = supportModules[0].module;
            if (firstSupport && firstSupport.aspects && firstSupport.aspects.length > 0) {
                const aspectColors: Record<string, number> = {
                    THERMAL: 0x3a2828, CRYO: 0x283038, RADIATION: 0x283a28, EMP: 0x38382a, VOID: 0x302838,
                };
                const color = aspectColors[firstSupport.aspects[0]] || 0x1a1a2e;
                this.shipSprite.setRegionTint('hull', color);
            }
        }

        this.updateStatsDisplay();
    }

    private calculateEffectiveBudgets(): { cmpMax: number; rctMax: number; cmpUsed: number; rctUsed: number } {
        let cmpUsed = 0, rctUsed = 0;
        let cmpBonus = 0, rctBonus = 0;

        this.fittedModules.forEach(fm => {
            if (fm.module) {
                const def = getModuleDefinition(fm.module.definitionId);
                if (def) {
                    cmpUsed += def.cmpCost;
                    rctUsed += def.rctCost;

                    // Check for budget-adding effects (Capacitor Banks)
                    if (def.passiveEffects) {
                        for (const eff of def.passiveEffects) {
                            if (eff.effectId === 'eff_add_fit_budget') {
                                const params = eff.params as { cmpAdd?: number; rctAdd?: number };
                                cmpBonus += params.cmpAdd || 0;
                                rctBonus += params.rctAdd || 0;
                            }
                        }
                    }
                }
            }
        });

        return {
            cmpMax: GAME_CONFIG.CMP_BUDGET + cmpBonus,
            rctMax: GAME_CONFIG.RCT_BUDGET + rctBonus,
            cmpUsed,
            rctUsed,
        };
    }

    private calculateFittingStats(): {
        shield: number;
        resists: Record<string, number>;
        damage: Record<string, number>;
        damageMultipliers: Record<string, number>;
        skills: Array<{ name: string; chargeCost: number; chargeSource: string }>;
    } {
        const modulesData = getModulesData();
        let shield = modulesData?.ship?.baseStats?.shieldMax || 75;
        const resists: Record<string, number> = {
            THERMAL: 0, CRYO: 0, RADIATION: 0, EMP: 0, VOID: 0,
        };
        const damage: Record<string, number> = {
            THERMAL: 0, CRYO: 0, RADIATION: 0, EMP: 0, VOID: 0,
        };
        // Track multipliers by weapon type (laser, railgun, missile, cryo, pulse)
        const weaponTypeMultipliers: Record<string, number> = {
            laser: 1.0, railgun: 1.0, missile: 1.0, cryo: 1.0, pulse: 1.0,
        };
        // For display, track which damage types have multipliers applied
        const damageMultipliers: Record<string, number> = {
            THERMAL: 1.0, CRYO: 1.0, RADIATION: 1.0, EMP: 1.0, VOID: 1.0,
        };
        const skills: Array<{ name: string; chargeCost: number; chargeSource: string }> = [];
        // Store weapon info for second pass
        const weapons: Array<{
            fm: FittedModule;
            def: ModuleDefinition;
            weaponKey: string;
            baseDamage: number;
        }> = [];

        const colorNames: Record<string, string> = { R: 'Red', B: 'Blue', G: 'Green', Y: 'Yellow', P: 'Purple' };

        // First pass: gather weapon type multipliers and collect weapon info
        for (const fm of this.fittedModules) {
            if (!fm.module) continue;
            const def = getModuleDefinition(fm.module.definitionId);
            if (!def) continue;

            const level = fm.module.level;
            const levelBonus = level - 1;
            const scaling = def.scaling as Record<string, number> | undefined;

            // Process passive effects
            if (def.passiveEffects) {
                for (const eff of def.passiveEffects) {
                    const p = eff.params as Record<string, unknown>;
                    if (eff.effectId === 'eff_add_shield_max') {
                        const baseAdd = (p.add as number) || 0;
                        const perLevel = scaling?.addPerLevel || 0;
                        shield += baseAdd + perLevel * levelBonus;
                    } else if (eff.effectId === 'eff_add_resist') {
                        const dt = p.damageType as string;
                        const baseResist = (p.resistAdd as number) || 0;
                        const perLevel = scaling?.resistPerLevel || 0;
                        const totalResist = baseResist + perLevel * levelBonus;
                        if (dt && resists[dt] !== undefined) {
                            resists[dt] = Math.min(0.6, resists[dt] + totalResist);
                        }
                    } else if (eff.effectId === 'eff_weapon_fire') {
                        // Store weapon info for second pass
                        const weaponKey = (p.weaponKey as string) || 'laser';
                        const baseWpnDmg = def.scaling?.baseDamage || (p.baseDamage as number) || 0;
                        const dmgPerLevel = def.scaling?.damagePerLevel || 0;
                        const totalDamage = baseWpnDmg + dmgPerLevel * levelBonus;
                        weapons.push({ fm, def, weaponKey, baseDamage: totalDamage });
                    } else if (eff.effectId === 'eff_weapon_damage_multiplier') {
                        // Multipliers are now keyed by weapon type
                        const wt = p.weaponType as string;
                        const baseMult = (p.multiplierAdd as number) || 0;
                        const perLevel = scaling?.damageMultPerLevel || 0;
                        const totalMult = baseMult + perLevel * levelBonus;
                        if (wt && weaponTypeMultipliers[wt] !== undefined) {
                            weaponTypeMultipliers[wt] += totalMult;
                        }
                    }
                }
            }

            // Process skills
            if (def.skill) {
                // Skill charge sources come from module's rolled aspects
                const aspectToOrb: Record<string, string> = {
                    THERMAL: 'R', CRYO: 'B', RADIATION: 'G', EMP: 'Y', VOID: 'P',
                };
                const orbColors = (fm.module.aspects || []).map(a => aspectToOrb[a] || 'R');
                const chargeSourceStr = orbColors.map(c => colorNames[c] || c).join('/');
                // Apply skill charge cost scaling
                const baseChargeCost = def.skill.chargeCost || 10;
                const chargeCostPerLevel = scaling?.chargeCostPerLevel || 0;
                const totalChargeCost = Math.max(1, Math.round(baseChargeCost + chargeCostPerLevel * levelBonus));
                skills.push({
                    name: def.name,
                    chargeCost: totalChargeCost,
                    chargeSource: chargeSourceStr,
                });
            }
        }

        // Second pass: calculate weapon damage with multipliers
        for (const wpn of weapons) {
            // Damage type comes from module's rolled aspects
            const damageType = wpn.fm.module!.aspects[0] || 'THERMAL';
            const multiplier = weaponTypeMultipliers[wpn.weaponKey] || 1.0;
            const finalDamage = wpn.baseDamage * multiplier;
            damage[damageType] = (damage[damageType] || 0) + finalDamage;
            // Track the multiplier for display (use highest if multiple weapons of same type)
            if (multiplier > damageMultipliers[damageType]) {
                damageMultipliers[damageType] = multiplier;
            }
        }

        return { shield, resists, damage, damageMultipliers, skills };
    }

    // CRT Reactor Display Gem Colors (shared with GameScene)
    private static readonly GEM_PALETTE: Record<OrbColor, { core: number; glow: number }> = {
        R: { core: 0xFF4A3D, glow: 0xB61F1A },
        B: { core: 0x2FA0FF, glow: 0x0F4C8A },
        G: { core: 0x2BFF9A, glow: 0x0F7A4F },
        Y: { core: 0xFFD24A, glow: 0x8A6A12 },
        P: { core: 0xB06CFF, glow: 0x4F1E86 },
        S: { core: 0xF2F6FF, glow: 0x7A8CA3 },
    };
    private static readonly BEZEL_FILL = 0x141821;
    private static readonly BEZEL_STROKE = 0x2A3140;
    private static readonly WINDOW_BORDER = 0x0B0E14;
    private static readonly GLYPH_COLOR = 0xD7DEE8;

    // Element type to OrbColor mapping
    private static readonly ELEMENT_TO_ORB: Record<string, OrbColor> = {
        THERMAL: 'R',
        CRYO: 'B',
        RADIATION: 'G',
        EMP: 'Y',
        VOID: 'P',
    };

    private drawCRTGem(graphics: Phaser.GameObjects.Graphics, x: number, y: number, orbColor: OrbColor, size: number, disabled = false): void {
        const palette = BaseScene.GEM_PALETTE[orbColor];
        const half = size / 2;
        const chamfer = size * 0.22;

        // Helper to get octagon points centered at origin
        const getOctagonPoints = (h: number, c: number): number[] => [
            -h + c, -h, h - c, -h, h, -h + c, h, h - c,
            h - c, h, -h + c, h, -h, h - c, -h, -h + c,
        ];

        // Translate all points to x,y center
        const translatePoints = (pts: number[]): number[] => {
            const result: number[] = [];
            for (let i = 0; i < pts.length; i += 2) {
                result.push(pts[i] + x, pts[i + 1] + y);
            }
            return result;
        };

        const outerPoints = translatePoints(getOctagonPoints(half, chamfer));
        const innerInset = half * 0.12;
        const innerHalf = half - innerInset;
        const innerChamfer = chamfer * 0.85;
        const innerPoints = translatePoints(getOctagonPoints(innerHalf, innerChamfer));

        // Layer 1: Outer bezel
        graphics.fillStyle(disabled ? 0x1a1a1a : BaseScene.BEZEL_FILL, 1);
        graphics.beginPath();
        graphics.moveTo(outerPoints[0], outerPoints[1]);
        for (let i = 2; i < outerPoints.length; i += 2) {
            graphics.lineTo(outerPoints[i], outerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Bezel stroke
        graphics.lineStyle(1.5, disabled ? 0x333333 : BaseScene.BEZEL_STROKE, 1);
        graphics.strokePath();

        // Inner shadow
        graphics.lineStyle(1, 0x000000, 0.3);
        graphics.beginPath();
        graphics.moveTo(innerPoints[0], innerPoints[1]);
        for (let i = 2; i < innerPoints.length; i += 2) {
            graphics.lineTo(innerPoints[i], innerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.strokePath();

        // Layer 2: Window
        const windowInset = half * 0.18;
        const windowHalf = half - windowInset;
        const windowChamfer = chamfer * 0.7;
        const windowPoints = translatePoints(getOctagonPoints(windowHalf, windowChamfer));

        graphics.fillStyle(disabled ? 0x0a0a0a : BaseScene.WINDOW_BORDER, 1);
        graphics.beginPath();
        graphics.moveTo(windowPoints[0], windowPoints[1]);
        for (let i = 2; i < windowPoints.length; i += 2) {
            graphics.lineTo(windowPoints[i], windowPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Layer 3: Emissive screen
        const screenInset = half * 0.22;
        const screenHalf = half - screenInset;
        const screenChamfer = chamfer * 0.6;
        const screenPoints = translatePoints(getOctagonPoints(screenHalf, screenChamfer));

        const coreColor = disabled ? 0x444444 : palette.core;
        graphics.fillStyle(coreColor, disabled ? 0.5 : 0.9);
        graphics.beginPath();
        graphics.moveTo(screenPoints[0], screenPoints[1]);
        for (let i = 2; i < screenPoints.length; i += 2) {
            graphics.lineTo(screenPoints[i], screenPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Layer 4: Glyph
        if (!disabled) {
            this.drawGemGlyph(graphics, x, y, orbColor, screenHalf * 0.65);
        }
    }

    private drawGemGlyph(graphics: Phaser.GameObjects.Graphics, x: number, y: number, color: OrbColor, size: number): void {
        graphics.lineStyle(1.5, BaseScene.GLYPH_COLOR, 0.4);

        switch (color) {
            case 'R': // Lightning
                const lw = size * 0.5, lh = size * 0.9;
                graphics.beginPath();
                graphics.moveTo(x + lw * 0.3, y - lh);
                graphics.lineTo(x - lw * 0.2, y - lh * 0.15);
                graphics.lineTo(x + lw * 0.25, y - lh * 0.1);
                graphics.lineTo(x - lw * 0.3, y + lh);
                graphics.strokePath();
                break;
            case 'B': // Waves
                for (let i = -1; i <= 1; i++) {
                    const waveY = y + i * size * 0.35;
                    graphics.beginPath();
                    graphics.moveTo(x - size * 0.6, waveY);
                    graphics.lineTo(x - size * 0.3, waveY - size * 0.12);
                    graphics.lineTo(x, waveY);
                    graphics.lineTo(x + size * 0.3, waveY + size * 0.12);
                    graphics.lineTo(x + size * 0.6, waveY);
                    graphics.strokePath();
                }
                break;
            case 'G': // Triangle
                const triSize = size * 0.75;
                graphics.beginPath();
                graphics.moveTo(x, y - triSize * 0.8);
                graphics.lineTo(x + triSize * 0.7, y + triSize * 0.5);
                graphics.lineTo(x - triSize * 0.7, y + triSize * 0.5);
                graphics.closePath();
                graphics.strokePath();
                break;
            case 'Y': // Diamond
                const dSize = size * 0.7;
                graphics.beginPath();
                graphics.moveTo(x, y - dSize);
                graphics.lineTo(x + dSize * 0.7, y);
                graphics.lineTo(x, y + dSize);
                graphics.lineTo(x - dSize * 0.7, y);
                graphics.closePath();
                graphics.strokePath();
                break;
            case 'P': // Hexagon
                const hexSize = size * 0.65;
                graphics.beginPath();
                for (let i = 0; i < 6; i++) {
                    const angle = (Math.PI / 3) * i - Math.PI / 2;
                    const px = x + Math.cos(angle) * hexSize;
                    const py = y + Math.sin(angle) * hexSize;
                    if (i === 0) graphics.moveTo(px, py);
                    else graphics.lineTo(px, py);
                }
                graphics.closePath();
                graphics.strokePath();
                break;
            case 'S': // Plus (shield)
                const pSize = size * 0.7;
                graphics.beginPath();
                graphics.moveTo(x, y - pSize);
                graphics.lineTo(x, y + pSize);
                graphics.strokePath();
                graphics.beginPath();
                graphics.moveTo(x - pSize, y);
                graphics.lineTo(x + pSize, y);
                graphics.strokePath();
                break;
        }
    }

    private updateStatsDisplay(): void {
        this.statsContainer.removeAll(true);

        const { cmpMax, rctMax, cmpUsed, rctUsed } = this.calculateEffectiveBudgets();
        const { shield, resists, damage, damageMultipliers, skills } = this.calculateFittingStats();

        // Accessibility: minimum 24px font, proper spacing
        const fontSize = '24px';
        const smallFontSize = '20px';
        const rowGap = 42;
        const barWidth = 160;
        const barHeight = 24;
        const gemSize = 18; // Larger CRT gems
        const gemSpacing = 72; // Wider spacing for bigger gems

        // Center the content
        const leftEdge = -200;
        let currentY = -90;

        // Element colors and names
        const elementColors: Record<string, number> = {
            THERMAL: 0xe74c3c, CRYO: 0x3498db, RADIATION: 0x2ecc71, EMP: 0xf1c40f, VOID: 0x9b59b6,
        };
        const elements = ['THERMAL', 'CRYO', 'RADIATION', 'EMP', 'VOID'];

        // Row 1: CMP bar
        const cmpLabel = this.add.text(leftEdge, currentY, 'CMP', {
            fontFamily: 'monospace', fontSize, color: this.colors.textSecondary,
        });
        cmpLabel.setOrigin(0, 0.5);
        this.statsContainer.add(cmpLabel);

        const cmpBarX = leftEdge + 70;
        const cmpBg = this.add.graphics();
        cmpBg.fillStyle(0x222233, 1);
        cmpBg.fillRoundedRect(cmpBarX, currentY - barHeight / 2, barWidth, barHeight, 4);
        cmpBg.fillStyle(cmpUsed > cmpMax ? 0xe74c3c : 0x2ecc71, 1);
        cmpBg.fillRoundedRect(cmpBarX, currentY - barHeight / 2, Math.min(cmpUsed / cmpMax, 1) * barWidth, barHeight, 4);
        this.statsContainer.add(cmpBg);

        const cmpText = this.add.text(cmpBarX + barWidth + 12, currentY, `${cmpUsed}/${cmpMax}`, {
            fontFamily: 'monospace', fontSize, color: cmpUsed > cmpMax ? '#e74c3c' : this.colors.textPrimary, fontStyle: 'bold',
        });
        cmpText.setOrigin(0, 0.5);
        this.statsContainer.add(cmpText);

        currentY += rowGap;

        // Row 2: RCT bar
        const rctLabel = this.add.text(leftEdge, currentY, 'RCT', {
            fontFamily: 'monospace', fontSize, color: this.colors.textSecondary,
        });
        rctLabel.setOrigin(0, 0.5);
        this.statsContainer.add(rctLabel);

        const rctBarX = leftEdge + 70;
        const rctBg = this.add.graphics();
        rctBg.fillStyle(0x222233, 1);
        rctBg.fillRoundedRect(rctBarX, currentY - barHeight / 2, barWidth, barHeight, 4);
        rctBg.fillStyle(rctUsed > rctMax ? 0xe74c3c : 0x3498db, 1);
        rctBg.fillRoundedRect(rctBarX, currentY - barHeight / 2, Math.min(rctUsed / rctMax, 1) * barWidth, barHeight, 4);
        this.statsContainer.add(rctBg);

        const rctText = this.add.text(rctBarX + barWidth + 12, currentY, `${rctUsed}/${rctMax}`, {
            fontFamily: 'monospace', fontSize, color: rctUsed > rctMax ? '#e74c3c' : this.colors.textPrimary, fontStyle: 'bold',
        });
        rctText.setOrigin(0, 0.5);
        this.statsContainer.add(rctText);

        currentY += rowGap;

        // Row 3: Shield
        const shieldLabel = this.add.text(leftEdge, currentY, 'SHIELD', {
            fontFamily: 'monospace', fontSize, color: this.colors.textSecondary,
        });
        shieldLabel.setOrigin(0, 0.5);
        this.statsContainer.add(shieldLabel);

        const shieldValue = this.add.text(leftEdge + 120, currentY, `${shield}`, {
            fontFamily: 'monospace', fontSize, color: '#ecf0f1', fontStyle: 'bold',
        });
        shieldValue.setOrigin(0, 0.5);
        this.statsContainer.add(shieldValue);

        currentY += rowGap;

        // Row 4: Resists (all 5 elements with CRT gems)
        const resistLabel = this.add.text(leftEdge, currentY, 'RESIST', {
            fontFamily: 'monospace', fontSize, color: this.colors.textSecondary,
        });
        resistLabel.setOrigin(0, 0.5);
        this.statsContainer.add(resistLabel);

        let resistX = leftEdge + 110;
        for (const el of elements) {
            const pct = Math.round(resists[el] * 100);
            const orbColor = BaseScene.ELEMENT_TO_ORB[el];
            const isDisabled = pct === 0;
            const textColor = isDisabled ? '#999999' : '#ffffff';

            const resistGem = this.add.graphics();
            this.drawCRTGem(resistGem, resistX, currentY, orbColor, gemSize, isDisabled);
            this.statsContainer.add(resistGem);

            const resistPct = this.add.text(resistX + gemSize / 2 + 8, currentY, `${pct}%`, {
                fontFamily: 'monospace', fontSize: smallFontSize, color: textColor,
            });
            resistPct.setOrigin(0, 0.5);
            this.statsContainer.add(resistPct);
            resistX += gemSpacing;
        }

        currentY += rowGap;

        // Row 5: Damage label with gems to the right on same row
        const damageLabel = this.add.text(leftEdge, currentY, 'DAMAGE', {
            fontFamily: 'monospace', fontSize, color: this.colors.textSecondary,
        });
        damageLabel.setOrigin(0, 0.5);
        this.statsContainer.add(damageLabel);

        // Check if we have any damage to display
        let hasDamage = false;
        for (const el of elements) {
            if (Math.round(damage[el]) > 0) {
                hasDamage = true;
                break;
            }
        }

        if (!hasDamage) {
            const noDmg = this.add.text(leftEdge + 120, currentY, '—', {
                fontFamily: 'monospace', fontSize, color: '#999999',
            });
            noDmg.setOrigin(0, 0.5);
            this.statsContainer.add(noDmg);
        } else {
            // Big damage gems to the right of label on same row (flow layout)
            const bigGemSize = 46;
            const gemGap = 24; // Gap between gem entries
            let damageX = leftEdge + 120;

            for (const el of elements) {
                const dmg = Math.round(damage[el]);
                const mult = damageMultipliers[el];
                if (dmg > 0) {
                    const orbColor = BaseScene.ELEMENT_TO_ORB[el];

                    // Gem centered at damageX
                    const damageGem = this.add.graphics();
                    this.drawCRTGem(damageGem, damageX, currentY, orbColor, bigGemSize, false);
                    this.statsContainer.add(damageGem);

                    // Damage value to right of gem
                    const textStartX = damageX + bigGemSize / 2 + 6;
                    const damageVal = this.add.text(textStartX, currentY, `${dmg}`, {
                        fontFamily: 'monospace', fontSize: '20px', color: '#ffffff', fontStyle: 'bold',
                    });
                    damageVal.setOrigin(0, 0.5);
                    this.statsContainer.add(damageVal);

                    let textEndX = textStartX + damageVal.width;

                    // Bonus to right of damage value if multiplier > 1.0
                    if (mult > 1.0) {
                        const bonusPct = `+${Math.round((mult - 1) * 100)}%`;
                        const bonusText = this.add.text(textEndX + 4, currentY, bonusPct, {
                            fontFamily: 'monospace', fontSize: '16px', color: '#2ecc71', fontStyle: 'bold',
                        });
                        bonusText.setOrigin(0, 0.5);
                        this.statsContainer.add(bonusText);
                        textEndX = textEndX + 4 + bonusText.width;
                    }

                    // Move to next entry position (flow layout with gap)
                    damageX = textEndX + gemGap + bigGemSize / 2;
                }
            }
        }
    }

    private updateFittingSlots(): void {
        this.slotsContainer.removeAll(true);
        this.slotContainers = [];

        const slotSize = this.gridCellSize;
        const gap = 30, slotGap = 10;

        const weaponSlots = this.fittedModules.filter(fm => fm.slotType === 'weapon');
        const supportSlots = this.fittedModules.filter(fm => fm.slotType === 'support');

        const totalWidth = weaponSlots.length * slotSize + gap + supportSlots.length * slotSize +
            (weaponSlots.length - 1) * slotGap + (supportSlots.length - 1) * slotGap;

        // Check if fit is valid (within effective budget including capacitor bonuses)
        const { cmpMax, rctMax, cmpUsed, rctUsed } = this.calculateEffectiveBudgets();
        const isValidFit = cmpUsed <= cmpMax && rctUsed <= rctMax;
        const hasFittedModule = this.fittedModules.some(fm => fm.module !== null);

        // Draw subtle border around the entire fitting area
        const areaBorder = this.add.graphics();
        const padding = 12;
        const areaWidth = totalWidth + padding * 2;
        const areaHeight = slotSize + padding * 2 + 40;  // Extra for labels
        const borderColor = hasFittedModule ? (isValidFit ? 0x2ecc71 : 0xe74c3c) : 0x3a3a5e;
        const borderAlpha = hasFittedModule ? 0.4 : 0.3;

        areaBorder.lineStyle(2, borderColor, borderAlpha);
        areaBorder.strokeRoundedRect(-areaWidth / 2, -areaHeight / 2, areaWidth, areaHeight, 8);
        this.slotsContainer.add(areaBorder);

        let currentX = -totalWidth / 2 + slotSize / 2;

        const weaponLabel = this.add.text(
            currentX + (weaponSlots.length * slotSize + (weaponSlots.length - 1) * slotGap) / 2 - slotSize / 2,
            -slotSize / 2 - 30, 'WEAPONS',
            { fontFamily: 'monospace', fontSize: this.fonts.small, color: '#ff6b6b', fontStyle: 'bold' }
        );
        weaponLabel.setOrigin(0.5);
        this.slotsContainer.add(weaponLabel);

        weaponSlots.forEach((fm) => {
            const slot = this.createFittingSlot(fm, slotSize);
            slot.setPosition(currentX, 0);
            this.slotsContainer.add(slot);
            this.slotContainers.push(slot);
            currentX += slotSize + slotGap;
        });

        currentX += gap - slotGap;

        const supportLabel = this.add.text(
            currentX + (supportSlots.length * slotSize + (supportSlots.length - 1) * slotGap) / 2 - slotSize / 2,
            -slotSize / 2 - 30, 'SUPPORT',
            { fontFamily: 'monospace', fontSize: this.fonts.small, color: '#74b9ff', fontStyle: 'bold' }
        );
        supportLabel.setOrigin(0.5);
        this.slotsContainer.add(supportLabel);

        supportSlots.forEach((fm) => {
            const slot = this.createFittingSlot(fm, slotSize);
            slot.setPosition(currentX, 0);
            this.slotsContainer.add(slot);
            this.slotContainers.push(slot);
            currentX += slotSize + slotGap;
        });

        // Draw blueprint-style connection lines from slots to ship regions
        this.drawConnectionLines(slotSize, gap, slotGap, weaponSlots.length, supportSlots.length);
    }

    private drawConnectionLines(
        slotSize: number,
        gap: number,
        slotGap: number,
        weaponCount: number,
        supportCount: number
    ): void {
        this.connectionLines.clear();

        // Get ship and slots container positions
        const panelX = this.MIDDLE_X + 8;
        const panelY = 8;
        const panelWidth = this.MIDDLE_WIDTH - 16;
        const panelHeight = GAME_CONFIG.HEIGHT - 16;
        const shipAreaHeight = panelHeight * 0.42;
        const statsAreaHeight = panelHeight * 0.23;
        const fittingAreaHeight = panelHeight * 0.35 - 50;

        const shipCenterX = panelX + panelWidth / 2;
        const shipCenterY = panelY + 50 + shipAreaHeight / 2;
        const slotsCenterX = panelX + panelWidth / 2;
        const slotsCenterY = panelY + 50 + shipAreaHeight + statsAreaHeight + fittingAreaHeight / 2;

        const totalWidth = weaponCount * slotSize + gap + supportCount * slotSize +
            (weaponCount - 1) * slotGap + (supportCount - 1) * slotGap;

        // Blueprint style: dashed lines with subtle color
        const lineColor = 0x4a5a7e;
        const lineAlpha = 0.35;
        const dashLength = 8;
        const gapLength = 6;

        // Calculate slot positions
        let currentX = -totalWidth / 2 + slotSize / 2;

        // Draw lines from weapon slots to wing/engine regions
        for (let i = 0; i < weaponCount; i++) {
            const slotX = slotsCenterX + currentX;
            const slotY = slotsCenterY - slotSize / 2 - 8;

            // Target points on ship wings (scaled by 1.6)
            const wingOffsetX = (i === 0 ? -80 : 80) * 1.6;
            const wingY = shipCenterY + 40 * 1.6;

            this.drawDashedLine(
                slotX, slotY,
                shipCenterX + wingOffsetX, wingY,
                lineColor, lineAlpha, dashLength, gapLength
            );

            // Small circle at ship connection point
            this.connectionLines.fillStyle(lineColor, lineAlpha + 0.2);
            this.connectionLines.fillCircle(shipCenterX + wingOffsetX, wingY, 4);

            currentX += slotSize + slotGap;
        }

        currentX += gap - slotGap;

        // Draw lines from support slots to hull regions
        for (let i = 0; i < supportCount; i++) {
            const slotX = slotsCenterX + currentX;
            const slotY = slotsCenterY - slotSize / 2 - 8;

            // Target points on ship hull (spread across the center, scaled by 1.6)
            const hullOffsetX = (i - 1) * 35 * 1.6;
            const hullY = shipCenterY - 20 * 1.6;

            this.drawDashedLine(
                slotX, slotY,
                shipCenterX + hullOffsetX, hullY,
                lineColor, lineAlpha, dashLength, gapLength
            );

            // Small circle at ship connection point
            this.connectionLines.fillStyle(lineColor, lineAlpha + 0.2);
            this.connectionLines.fillCircle(shipCenterX + hullOffsetX, hullY, 4);

            currentX += slotSize + slotGap;
        }
    }

    private drawDashedLine(
        x1: number, y1: number,
        x2: number, y2: number,
        color: number, alpha: number,
        dashLength: number, gapLength: number
    ): void {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const unitX = dx / distance;
        const unitY = dy / distance;

        let currentDist = 0;
        let drawing = true;

        this.connectionLines.lineStyle(2, color, alpha);

        while (currentDist < distance) {
            const segmentLength = drawing ? dashLength : gapLength;
            const endDist = Math.min(currentDist + segmentLength, distance);

            if (drawing) {
                const startX = x1 + unitX * currentDist;
                const startY = y1 + unitY * currentDist;
                const endX = x1 + unitX * endDist;
                const endY = y1 + unitY * endDist;

                this.connectionLines.beginPath();
                this.connectionLines.moveTo(startX, startY);
                this.connectionLines.lineTo(endX, endY);
                this.connectionLines.strokePath();
            }

            currentDist = endDist;
            drawing = !drawing;
        }
    }

    private createFittingSlot(fittedModule: FittedModule, size: number): Phaser.GameObjects.Container {
        const container = this.add.container(0, 0);

        if (fittedModule.module) {
            // Check if this module is currently selected
            const isSelected = this.selectedModule?.instanceId === fittedModule.module.instanceId;

            // Don't use isFitted styling - slots keep their module's rarity border color
            const card = this.moduleRenderer.createModuleCard(fittedModule.module, {
                size, showLevel: true, showAffinities: true, isFitted: false, isSelected,
            });

            if (card) {
                card.setInteractive({ useHandCursor: true });
                card.setData('playerModule', fittedModule.module);
                card.setData('fittedSlot', fittedModule);

                // Track pointer state for fitted slots
                let slotPointerDownTime = 0;
                let slotPointerDownX = 0;
                let slotPointerDownY = 0;
                let slotIsDragging = false;

                card.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
                    slotPointerDownTime = Date.now();
                    slotPointerDownX = pointer.x;
                    slotPointerDownY = pointer.y;
                    slotIsDragging = false;
                });

                card.on('pointermove', (pointer: Phaser.Input.Pointer) => {
                    if (slotPointerDownTime === 0) return;

                    const dx = pointer.x - slotPointerDownX;
                    const dy = pointer.y - slotPointerDownY;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    // Start drag if moved enough
                    if (!slotIsDragging && dist > this.DRAG_THRESHOLD) {
                        slotIsDragging = true;
                        this.startDraggingFromSlot(fittedModule, pointer);
                    }

                    // Update drag position
                    if (slotIsDragging && this.draggedModuleCard) {
                        this.draggedModuleCard.setPosition(pointer.x, pointer.y);
                        this.highlightValidSlots(fittedModule.module);
                    }
                });

                card.on('pointerup', (pointer: Phaser.Input.Pointer) => {
                    if (slotIsDragging) {
                        this.endDragging(pointer);
                    } else {
                        // Click - select the module for upgrade/evolve
                        if (fittedModule.module) {
                            this.selectModule(fittedModule.module);
                        }
                    }
                    slotPointerDownTime = 0;
                    slotIsDragging = false;
                });

                card.on('pointerout', (pointer: Phaser.Input.Pointer) => {
                    // Continue drag even if pointer leaves the card
                    if (slotIsDragging && this.isDragging) {
                        // Drag continues, handled by global pointermove
                    } else {
                        slotPointerDownTime = 0;
                    }
                });

                container.add(card);
            }
        } else {
            const empty = this.moduleRenderer.createEmptySlot(size, fittedModule.slotType);
            container.add(empty);
        }

        container.setData('slotType', fittedModule.slotType);
        container.setData('isEmpty', fittedModule.module === null);
        container.setData('fittedModule', fittedModule);
        container.setSize(size, size);
        return container;
    }

    private startDraggingFromSlot(fittedModule: FittedModule, pointer: Phaser.Input.Pointer): void {
        if (!fittedModule.module) return;

        this.isDragging = true;
        this.draggedModule = fittedModule.module;
        this.draggedFromSlot = fittedModule;

        this.draggedModuleCard = this.moduleRenderer.createModuleCard(fittedModule.module, {
            size: this.gridCellSize,
            showLevel: true,
            showAffinities: true,
        });

        if (this.draggedModuleCard) {
            this.draggedModuleCard.setPosition(pointer.x, pointer.y);
            this.draggedModuleCard.setAlpha(0.9);
            this.draggedModuleCard.setDepth(1000);
        }
    }

    private unequipModule(fittedModule: FittedModule): void {
        fittedModule.module = null;
        this.updateWeaponGrid();
        this.updateSupportGrid();
        this.updateFittingSlots();
        this.updateShipVisual();
        this.saveLoadout(); // Auto-save loadout
    }

    // ==================== RIGHT PANEL ====================

    private createRightPanel(): void {
        const panelX = this.RIGHT_X + 8;
        const panelY = 8;
        const panelWidth = this.RIGHT_WIDTH - 16;
        const panelHeight = GAME_CONFIG.HEIGHT - 16;

        // Clear old mutator buttons if returning to scene
        this.mutatorButtons = [];

        const panelGraphics = this.add.graphics();
        this.ui.drawBeveledPanel(panelGraphics, panelX, panelY, panelWidth, panelHeight, 'RUN SCANNER');

        // Calculate component heights
        const titleHeight = 50; // Space for panel title
        const mutatorButtonHeight = 44;
        const mutatorRowGap = 8;
        const mutatorSectionHeight = (mutatorButtonHeight * 2) + mutatorRowGap + 24; // 2 rows + gap + top/bottom margins
        const gapAfterMutators = 16;
        const threatSliderHeight = 44 + 35; // Slider + label height
        const gapAfterThreat = 16;
        const mainButtonsHeight = 80;
        const gapAfterMainButtons = 16;
        const bottomButtonsHeight = 50;
        const bottomMargin = 16;

        // Calculate map area: whatever space is left
        const usedSpace = titleHeight + mutatorSectionHeight + gapAfterMutators +
                         threatSliderHeight + gapAfterThreat + mainButtonsHeight +
                         gapAfterMainButtons + bottomButtonsHeight + bottomMargin;
        const mapAreaHeight = panelHeight - usedSpace;

        // Create map bounds rectangle for clipping
        this.mapBounds = new Phaser.Geom.Rectangle(
            panelX + 16,
            panelY + titleHeight,
            panelWidth - 32,
            mapAreaHeight
        );

        // Position map container at center of bounds
        this.mapContainer = this.add.container(
            this.mapBounds.x + this.mapBounds.width / 2,
            this.mapBounds.y + this.mapBounds.height / 2
        );

        // Apply geometry mask to constrain map rendering (no visible fill needed)
        this.mapMask = this.add.graphics();
        this.mapMask.fillStyle(0xffffff);
        this.mapMask.fillRect(this.mapBounds.x, this.mapBounds.y, this.mapBounds.width, this.mapBounds.height);
        const mask = this.mapMask.createGeometryMask();
        this.mapContainer.setMask(mask);
        this.mapMask.setVisible(false); // Hide the mask graphic itself

        // Create controls container - NOT centered, positioned at absolute Y
        const controlsStartY = this.mapBounds.y + mapAreaHeight;
        this.controlsContainer = this.add.container(panelX + panelWidth / 2, 0);

        // Stack components vertically from controlsStartY
        let currentY = controlsStartY;

        // Mutator buttons
        currentY += 12; // Top margin
        this.createMutatorButtons(panelWidth - 32, currentY);
        currentY += mutatorSectionHeight;

        // Gap
        currentY += gapAfterMutators;

        // Threat slider
        this.createThreatSlider(currentY, panelWidth - 32);
        currentY += threatSliderHeight;

        // Gap
        currentY += gapAfterThreat;

        // Main buttons (Reroll/Start)
        this.createMainButtons(currentY, panelWidth - 32);
        currentY += mainButtonsHeight;

        // Gap
        currentY += gapAfterMainButtons;

        // Bottom buttons (Fullscreen/Exit)
        this.createBottomButtons(currentY, panelWidth - 32);
    }

    private updateMapPreview(): void {
        this.mapContainer.removeAll(true);

        // Use actual map bounds instead of hardcoded percentages
        const mapAreaHeight = this.mapBounds.height;
        const panelWidth = this.RIGHT_WIDTH - 48;

        const nodeRadius = 28;

        // Reserve space at top for threat level + boss info (INSIDE the bounds)
        const threatLabelHeight = 32; // Font large height
        const bossInfoHeight = 80; // Boss info display height
        const topReservedSpace = threatLabelHeight + bossInfoHeight + 16; // 16px padding

        // Reserve space at bottom for node labels
        const bottomReservedSpace = 30; // Space for START node label below

        // Calculate available space for actual map nodes
        const availableMapHeight = mapAreaHeight - topReservedSpace - bottomReservedSpace;

        // Position nodes within the available space
        // Map content starts at -mapAreaHeight/2 + topReservedSpace
        const mapContentTop = -mapAreaHeight / 2 + topReservedSpace;
        const mapContentBottom = mapAreaHeight / 2 - bottomReservedSpace;

        const startY = mapContentBottom - nodeRadius - 20; // 20px margin from bottom
        const bossY = mapContentTop + nodeRadius + 20; // 20px margin from top, no label above boss

        // Evenly distribute rows in available space
        const totalVerticalSpace = startY - bossY;
        const rowSpacing = totalVerticalSpace / 4;

        const row1Y = startY - rowSpacing;
        const row2Y = startY - rowSpacing * 2;
        const row3Y = startY - rowSpacing * 3;

        const branchSpread = panelWidth * 0.35;

        const lines = this.add.graphics();
        lines.lineStyle(4, 0x4a4a6e, 0.8);
        this.mapContainer.add(lines);

        // Threat level and boss info at the TOP (inside bounds)
        const runThreat = this.runPlan?.threatLevel || 1;
        const threatLabelY = -mapAreaHeight / 2 + 20; // 20px from top edge
        const threatLabel = this.add.text(0, threatLabelY, `THREAT ${runThreat}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.large,
            color: this.getThreatColor(runThreat),
            fontStyle: 'bold',
        });
        threatLabel.setOrigin(0.5);
        this.mapContainer.add(threatLabel);

        // Boss info below threat level
        const bossInfoY = threatLabelY + 40;
        this.createBossInfoDisplay(0, bossInfoY, panelWidth);

        // Map nodes
        const startNode = this.createMapNode(0, startY, 'start', nodeRadius, 'START');
        this.mapContainer.add(startNode);

        const combat1 = this.createMapNode(-branchSpread, row1Y, 'combat', nodeRadius, 'COMBAT');
        const combat2 = this.createMapNode(branchSpread, row1Y, 'combat', nodeRadius, 'COMBAT');
        this.mapContainer.add(combat1);
        this.mapContainer.add(combat2);

        lines.lineBetween(0, startY - nodeRadius - 10, -branchSpread, row1Y + nodeRadius);
        lines.lineBetween(0, startY - nodeRadius - 10, branchSpread, row1Y + nodeRadius);

        const elite = this.createMapNode(-branchSpread, row2Y, 'elite', nodeRadius, 'ELITE');
        const combat3 = this.createMapNode(branchSpread, row2Y, 'combat', nodeRadius, 'COMBAT');
        this.mapContainer.add(elite);
        this.mapContainer.add(combat3);

        // Straight paths (stay on same side)
        lines.lineBetween(-branchSpread, row1Y - nodeRadius - 10, -branchSpread, row2Y + nodeRadius);
        lines.lineBetween(branchSpread, row1Y - nodeRadius - 10, branchSpread, row2Y + nodeRadius);
        // Crossover paths (switch sides)
        lines.lineBetween(-branchSpread, row1Y - nodeRadius - 10, branchSpread, row2Y + nodeRadius);
        lines.lineBetween(branchSpread, row1Y - nodeRadius - 10, -branchSpread, row2Y + nodeRadius);

        const treasure = this.createMapNode(-branchSpread, row3Y, 'treasure', nodeRadius, 'LOOT');
        const combat4 = this.createMapNode(branchSpread, row3Y, 'combat', nodeRadius, 'COMBAT');
        this.mapContainer.add(treasure);
        this.mapContainer.add(combat4);

        // Straight paths only (no crossover from row 2 to row 3)
        lines.lineBetween(-branchSpread, row2Y - nodeRadius - 10, -branchSpread, row3Y + nodeRadius);
        lines.lineBetween(branchSpread, row2Y - nodeRadius - 10, branchSpread, row3Y + nodeRadius);

        // Boss node (no label, info shown at top)
        const bossAspect = this.runPlan?.boss.aspects[0];
        const bossColor = bossAspect ? ASPECT_COLORS[bossAspect] : 0x9b59b6;
        const boss = this.createMapNode(0, bossY, 'boss', nodeRadius + 6, '', bossColor);
        this.mapContainer.add(boss);

        lines.lineBetween(-branchSpread, row3Y - nodeRadius - 10, 0, bossY + nodeRadius + 6);
        lines.lineBetween(branchSpread, row3Y - nodeRadius - 10, 0, bossY + nodeRadius + 6);
    }

    private createBossInfoDisplay(x: number, y: number, panelWidth: number): void {
        if (!this.runPlan) return;

        const boss = this.runPlan.boss;
        const container = this.add.container(x, y);
        this.mapContainer.add(container);

        // Boss name + blueprint reward indicator
        const nameText = this.add.text(0, 0, boss.name.toUpperCase(), {
            fontFamily: 'monospace',
            fontSize: this.fonts.medium,
            color: this.colors.textPrimary,
            fontStyle: 'bold',
        });
        nameText.setOrigin(0, 0.5);

        // Blueprint drop indicator (icon + W/S) to the right of name
        const isWeaponDrop = boss.blueprintDrop === 'bp_weapon';
        const bpLabel = isWeaponDrop ? 'W' : 'S';
        const bpColor = isWeaponDrop ? 0xe74c3c : 0x3498db;

        const bpIcon = this.add.graphics();
        const iconSize = 14;
        const iconX = nameText.width / 2 + 8;
        const iconY = 0;

        // Blueprint icon (small scroll)
        bpIcon.fillStyle(bpColor, 1);
        bpIcon.fillRoundedRect(iconX - iconSize / 2, iconY - iconSize / 2, iconSize, iconSize, 2);
        bpIcon.fillStyle(0x1a1a2e, 1);
        bpIcon.fillRect(iconX - 3, iconY - 3, 6, 2);
        bpIcon.fillRect(iconX - 3, iconY + 1, 6, 2);
        container.add(bpIcon);

        const bpText = this.add.text(iconX + iconSize / 2 + 3, 0, bpLabel, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: isWeaponDrop ? '#e74c3c' : '#3498db',
            fontStyle: 'bold',
        });
        bpText.setOrigin(0, 0.5);
        container.add(bpText);

        // Center the whole name + bp indicator group
        const totalWidth = nameText.width + 8 + iconSize + 3 + bpText.width;
        nameText.setX(-totalWidth / 2);
        bpIcon.setX(-totalWidth / 2 + nameText.width + 8 + iconSize / 2 - iconX);
        bpText.setX(-totalWidth / 2 + nameText.width + 8 + iconSize + 3);

        container.add(nameText);

        // HP display - scaled by threat level
        const multipliers = getThreatMultipliers(this.runPlan.threatLevel);
        const scaledHP = Math.round(boss.baseHP * multipliers.hpMult);
        const hpText = this.add.text(0, 28, `HP: ${scaledHP}`, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textSecondary,
        });
        hpText.setOrigin(0.5);
        container.add(hpText);

        // Aspect gems (CRT reactor style)
        const gemSize = 18;
        const gemSpacing = 8;
        const aspects = boss.aspects;
        const totalGemsWidth = aspects.length * gemSize + (aspects.length - 1) * gemSpacing;
        let gemX = -totalGemsWidth / 2 + gemSize / 2;

        for (const aspect of aspects) {
            const orbColor = BaseScene.ELEMENT_TO_ORB[aspect];
            if (orbColor) {
                const gem = this.add.graphics();
                this.drawCRTGem(gem, gemX, 52, orbColor, gemSize, false);
                container.add(gem);
            }
            gemX += gemSize + gemSpacing;
        }
    }

    private createMapNode(
        x: number, y: number,
        type: 'start' | 'combat' | 'elite' | 'boss' | 'treasure',
        radius: number, label: string,
        colorOverride?: number
    ): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        const graphics = this.add.graphics();

        const colors: Record<string, number> = {
            start: 0x2ecc71, combat: 0xe74c3c, elite: 0xe67e22, boss: 0x9b59b6, treasure: 0xf1c40f,
        };
        const nodeColor = colorOverride ?? colors[type];

        graphics.fillStyle(nodeColor, 0.3);
        graphics.fillCircle(0, 0, radius + 8);
        graphics.fillStyle(nodeColor, 1);
        graphics.fillCircle(0, 0, radius);
        graphics.fillStyle(0x1a1a2e, 0.5);
        graphics.fillCircle(0, 0, radius * 0.55);

        container.add(graphics);

        const text = this.add.text(0, -radius - 18, label, {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textPrimary,
            fontStyle: 'bold',
        });
        text.setOrigin(0.5);
        container.add(text);

        return container;
    }

    private createMainButtons(absoluteY: number, width: number): void {
        const buttonWidth = (width - 20) / 2;
        const buttonHeight = 80;

        this.createRerollButton(-buttonWidth / 2 - 5, absoluteY, buttonWidth, buttonHeight);
        this.createStartRunButton(buttonWidth / 2 + 5, absoluteY, buttonWidth, buttonHeight);
    }

    private createBottomButtons(absoluteY: number, width: number): void {
        const smallButtonWidth = (width - 20) / 2;
        const smallButtonHeight = 50;

        this.createControlButton(-smallButtonWidth / 2 - 5, absoluteY, 'FULLSCREEN', smallButtonWidth, smallButtonHeight, 0x6c5ce7, () => this.toggleFullscreen());
        this.createControlButton(smallButtonWidth / 2 + 5, absoluteY, 'EXIT', smallButtonWidth, smallButtonHeight, 0x636e72, () => this.showExitConfirmation());
    }

    private createMutatorButtons(width: number, absoluteY: number): void {
        const buttonWidth = (width - 24) / 4; // 4 buttons per row, 8px gaps
        const buttonHeight = 44; // Accessibility minimum
        const rowGap = 8;

        // Load mutator definitions
        const mutators = this.getMutatorDefinitions();

        // Create 2 rows × 4 buttons
        for (let row = 0; row < 2; row++) {
            for (let col = 0; col < 4; col++) {
                const index = row * 4 + col;
                if (index >= mutators.length) break;

                const mutator = mutators[index];
                const x = -width / 2 + (col * (buttonWidth + 8)) + buttonWidth / 2;
                const y = absoluteY + (row * (buttonHeight + rowGap));

                const button = this.createMutatorButton(
                    x, y, buttonWidth, buttonHeight, mutator
                );
                this.mutatorButtons.push(button);
                this.controlsContainer.add(button);
            }
        }
    }

    private getMutatorDefinitions(): any[] {
        // For now, return hardcoded mutators until we load from file
        // This will be replaced when we load from JSON in the preload phase
        return [
            { id: 'mut_overclocked', shortName: 'OVERCLOCK', name: 'Overclocked Reactors', description: '+25% weapon damage, -10% max shield' },
            { id: 'mut_efficient_caps', shortName: 'EFFICIENT', name: 'Efficient Capacitors', description: '-2 charge cost on all weapons' },
            { id: 'mut_extended_warranty', shortName: 'WARRANTY', name: 'Extended Warranty', description: '+50% shield from S orbs' },
            { id: 'mut_cascade_master', shortName: 'CASCADE', name: 'Cascade Mastery', description: '+10% damage per cascade in a move' },
            { id: 'mut_radiation_leak', shortName: 'RAD LEAK', name: 'Radiation Leak', description: 'S orbs don\'t spawn (rely on skills)' },
            { id: 'mut_solar_flare', shortName: 'SOLAR', name: 'Solar Flare', description: '-1s move time' },
            { id: 'mut_aggressive_hostiles', shortName: 'AGGRO', name: 'Aggressive Hostiles', description: 'Enemy cooldowns -1' },
            { id: 'mut_glass_cannon', shortName: 'GLASS', name: 'Glass Cannon', description: '+100% damage dealt/taken' },
        ];
    }

    private createMutatorButton(
        x: number, y: number,
        width: number, height: number,
        mutator: any
    ): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);

        // Background
        const bg = this.add.graphics();
        const isSelected = this.selectedMutators.has(mutator.id);
        const color = isSelected ? 0x3498db : 0x2c2c3e;
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-width / 2, -height / 2, width, height, 6);

        // Border for selected state
        if (isSelected) {
            bg.lineStyle(2, 0x5dade2, 1);
            bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 6);
        }

        // Label (compact to fit)
        const label = this.add.text(0, 0, mutator.shortName, {
            fontFamily: 'monospace',
            fontSize: '18px',
            color: '#ffffff',
            fontStyle: 'bold',
            align: 'center',
        });
        label.setOrigin(0.5);
        label.setWordWrapWidth(width - 8);

        container.add([bg, label]);
        container.setSize(width, height);
        container.setInteractive({ useHandCursor: true });

        // Toggle selection on click
        container.on('pointerdown', () => {
            this.toggleMutator(mutator.id);
            this.updateMutatorButtons();
        });

        // Store mutator reference
        container.setData('mutatorId', mutator.id);
        container.setData('mutator', mutator);

        return container;
    }

    private toggleMutator(mutatorId: string): void {
        if (this.selectedMutators.has(mutatorId)) {
            this.selectedMutators.delete(mutatorId);
        } else {
            // Optional: Limit max mutators (e.g., max 3)
            if (this.selectedMutators.size < 3) {
                this.selectedMutators.add(mutatorId);
            }
        }
    }

    private updateMutatorButtons(): void {
        this.mutatorButtons.forEach(button => {
            // Safety check: ensure button and its graphics still exist
            if (!button || button.length === 0) return;

            const mutatorId = button.getData('mutatorId') as string;
            const isSelected = this.selectedMutators.has(mutatorId);

            // Update background color
            const bg = button.getAt(0) as Phaser.GameObjects.Graphics;
            if (!bg) return; // Graphics object doesn't exist

            const width = button.width;
            const height = button.height;

            bg.clear();
            bg.fillStyle(isSelected ? 0x3498db : 0x2c2c3e, 1);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, 6);

            if (isSelected) {
                bg.lineStyle(2, 0x5dade2, 1);
                bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 6);
            }
        });
    }

    private toggleFullscreen(): void {
        if (this.scale.isFullscreen) {
            this.scale.stopFullscreen();
        } else {
            this.scale.startFullscreen();
        }
    }

    private showExitConfirmation(): void {
        // Dark overlay
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.8);
        overlay.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
        overlay.setDepth(1000);

        // Modal container
        const modalWidth = 400;
        const modalHeight = 200;
        const modalX = GAME_CONFIG.WIDTH / 2;
        const modalY = GAME_CONFIG.HEIGHT / 2;

        const modalBg = this.add.graphics();
        modalBg.fillStyle(0x1a1a2e, 1);
        modalBg.fillRoundedRect(modalX - modalWidth / 2, modalY - modalHeight / 2, modalWidth, modalHeight, 12);
        modalBg.lineStyle(3, 0x4a4a6e, 1);
        modalBg.strokeRoundedRect(modalX - modalWidth / 2, modalY - modalHeight / 2, modalWidth, modalHeight, 12);
        modalBg.setDepth(1001);

        // Title
        const title = this.add.text(modalX, modalY - 50, 'EXIT TO MENU?', {
            fontFamily: 'monospace',
            fontSize: this.fonts.large,
            color: this.colors.textPrimary,
            fontStyle: 'bold',
        });
        title.setOrigin(0.5);
        title.setDepth(1002);

        // Subtitle
        const subtitle = this.add.text(modalX, modalY - 10, 'Your loadout will be saved.', {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: this.colors.textSecondary,
        });
        subtitle.setOrigin(0.5);
        subtitle.setDepth(1002);

        // Buttons
        const btnWidth = 140;
        const btnHeight = 50;
        const btnY = modalY + 50;

        // Cancel button
        const cancelBg = this.add.graphics();
        cancelBg.fillStyle(0x636e72, 1);
        cancelBg.fillRoundedRect(modalX - btnWidth - 20, btnY - btnHeight / 2, btnWidth, btnHeight, 8);
        cancelBg.setDepth(1002);

        const cancelText = this.add.text(modalX - btnWidth / 2 - 20, btnY, 'CANCEL', {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: '#ffffff',
            fontStyle: 'bold',
        });
        cancelText.setOrigin(0.5);
        cancelText.setDepth(1003);

        const cancelZone = this.add.rectangle(modalX - btnWidth / 2 - 20, btnY, btnWidth, btnHeight, 0x000000, 0);
        cancelZone.setDepth(1004);
        cancelZone.setInteractive({ useHandCursor: true });
        cancelZone.on('pointerdown', () => {
            overlay.destroy();
            modalBg.destroy();
            title.destroy();
            subtitle.destroy();
            cancelBg.destroy();
            cancelText.destroy();
            cancelZone.destroy();
            exitBg.destroy();
            exitText.destroy();
            exitZone.destroy();
        });

        // Exit button
        const exitBg = this.add.graphics();
        exitBg.fillStyle(0xe74c3c, 1);
        exitBg.fillRoundedRect(modalX + 20, btnY - btnHeight / 2, btnWidth, btnHeight, 8);
        exitBg.setDepth(1002);

        const exitText = this.add.text(modalX + btnWidth / 2 + 20, btnY, 'EXIT', {
            fontFamily: 'monospace',
            fontSize: this.fonts.small,
            color: '#ffffff',
            fontStyle: 'bold',
        });
        exitText.setOrigin(0.5);
        exitText.setDepth(1003);

        const exitZone = this.add.rectangle(modalX + btnWidth / 2 + 20, btnY, btnWidth, btnHeight, 0x000000, 0);
        exitZone.setDepth(1004);
        exitZone.setInteractive({ useHandCursor: true });
        exitZone.on('pointerdown', () => {
            this.scene.start('MenuScene');
        });
    }

    private createThreatSlider(y: number, width: number): void {
        this.threatText = this.add.text(0, y - 35, `SELECT: ${this.threatLevel}`, {
            fontFamily: 'monospace', fontSize: this.fonts.medium, color: this.getThreatColor(this.threatLevel), fontStyle: 'bold',
        });
        this.threatText.setOrigin(0.5);
        this.controlsContainer.add(this.threatText);

        const sliderHeight = 44;
        this.threatSliderBg = this.add.graphics();
        this.updateThreatSlider(width, sliderHeight, y);
        this.controlsContainer.add(this.threatSliderBg);

        for (let i = 1; i <= 10; i++) {
            const segWidth = width / 10;
            const segX = -width / 2 + (i - 0.5) * segWidth;
            const zone = this.add.rectangle(segX, y, segWidth, sliderHeight, 0x000000, 0);
            zone.setInteractive({ useHandCursor: true });
            zone.on('pointerdown', () => {
                if (this.threatLevel === i) return; // No change
                this.threatLevel = i;
                this.updateThreatSlider(width, sliderHeight, y);
                // Update button costs
                this.updateRerollButton();
                this.updateStartRunButton();
                // Regenerate run at new threat level but keep the same boss
                this.tweens.add({
                    targets: this.mapContainer, alpha: 0, duration: 150,
                    onComplete: () => {
                        const currentBoss = this.runPlan?.boss;
                        this.runPlan = generateRun(this.threatLevel, undefined, currentBoss);
                        this.updateMapPreview();
                        this.tweens.add({ targets: this.mapContainer, alpha: 1, duration: 150 });
                    },
                });
            });
            this.controlsContainer.add(zone);
        }
    }

    private updateThreatSlider(width: number, height: number, y: number): void {
        this.threatSliderBg.clear();
        this.threatSliderBg.fillStyle(0x222233, 1);
        this.threatSliderBg.fillRoundedRect(-width / 2, y - height / 2, width, height, 6);

        const segWidth = width / 10;
        for (let i = 1; i <= 10; i++) {
            const segX = -width / 2 + (i - 1) * segWidth + 3;
            this.threatSliderBg.fillStyle(this.getThreatColorNum(i), i <= this.threatLevel ? 1 : 0.2);
            this.threatSliderBg.fillRoundedRect(segX, y - height / 2 + 3, segWidth - 6, height - 6, 3);
        }

        if (this.threatText) {
            this.threatText.setText(`SELECT: ${this.threatLevel}`);
            this.threatText.setColor(this.getThreatColor(this.threatLevel));
        }
    }

    private getThreatColor(level: number): string {
        if (level <= 3) return '#2ecc71';
        if (level <= 6) return '#f1c40f';
        if (level <= 8) return '#e67e22';
        return '#e74c3c';
    }

    private getThreatColorNum(level: number): number {
        if (level <= 3) return 0x2ecc71;
        if (level <= 6) return 0xf1c40f;
        if (level <= 8) return 0xe67e22;
        return 0xe74c3c;
    }

    private getRerollCost(): number {
        return 10 * this.threatLevel;
    }

    private createRerollButton(x: number, y: number, width: number, height: number): void {
        const cost = this.getRerollCost();
        const canAfford = this.playerScrap >= cost;
        const color = canAfford ? 0x3498db : 0x444455;

        this.rerollBg = this.add.graphics();
        this.rerollBg.fillStyle(color, 1);
        this.rerollBg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);

        this.rerollLabel = this.add.text(0, 0, `RE-ROLL ${cost}`, {
            fontFamily: 'monospace', fontSize: this.fonts.large, color: '#ffffff', fontStyle: 'bold',
        });
        this.rerollLabel.setOrigin(0.5);

        this.rerollButton = this.add.container(x, y, [this.rerollBg, this.rerollLabel]);
        this.rerollButton.setSize(width, height);
        this.rerollButton.setInteractive({ useHandCursor: true });

        this.rerollButton.on('pointerover', () => {
            const hoverCost = this.getRerollCost();
            const hoverCanAfford = this.playerScrap >= hoverCost;
            const hoverColor = hoverCanAfford ? 0x3498db : 0x444455;
            this.rerollBg.clear();
            this.rerollBg.fillStyle(hoverColor, 0.7);
            this.rerollBg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        this.rerollButton.on('pointerout', () => {
            this.updateRerollButton(width, height);
        });

        this.rerollButton.on('pointerdown', () => this.rerollMap());
        this.controlsContainer.add(this.rerollButton);
    }

    private updateRerollButton(width?: number, height?: number): void {
        const w = width || this.rerollButton.width;
        const h = height || this.rerollButton.height;
        const cost = this.getRerollCost();
        const canAfford = this.playerScrap >= cost;
        const color = canAfford ? 0x3498db : 0x444455;

        this.rerollBg.clear();
        this.rerollBg.fillStyle(color, 1);
        this.rerollBg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
        this.rerollLabel.setText(`RE-ROLL ${cost}`);
    }

    private getRunCost(): number {
        // Free at threat level 1, otherwise 10 * threat level
        return this.threatLevel > 1 ? 10 * this.threatLevel : 0;
    }

    private createStartRunButton(x: number, y: number, width: number, height: number): void {
        const cost = this.getRunCost();
        const canAfford = this.playerScrap >= cost || cost === 0;
        const color = canAfford ? 0x2ecc71 : 0x444455;

        this.startRunBg = this.add.graphics();
        this.startRunBg.fillStyle(color, 1);
        this.startRunBg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);

        const labelText = cost > 0 ? `START ${cost}` : 'START RUN';
        this.startRunLabel = this.add.text(0, 0, labelText, {
            fontFamily: 'monospace', fontSize: this.fonts.large, color: '#ffffff', fontStyle: 'bold',
        });
        this.startRunLabel.setOrigin(0.5);

        this.startRunButton = this.add.container(x, y, [this.startRunBg, this.startRunLabel]);
        this.startRunButton.setSize(width, height);
        this.startRunButton.setInteractive({ useHandCursor: true });

        this.startRunButton.on('pointerover', () => {
            const hoverCost = this.getRunCost();
            const hoverCanAfford = this.playerScrap >= hoverCost || hoverCost === 0;
            const hoverColor = hoverCanAfford ? 0x2ecc71 : 0x444455;
            this.startRunBg.clear();
            this.startRunBg.fillStyle(hoverColor, 0.7);
            this.startRunBg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        this.startRunButton.on('pointerout', () => {
            this.updateStartRunButton(width, height);
        });

        this.startRunButton.on('pointerdown', () => this.startRun());
        this.controlsContainer.add(this.startRunButton);
    }

    private updateStartRunButton(width?: number, height?: number): void {
        const w = width || this.startRunButton.width;
        const h = height || this.startRunButton.height;
        const cost = this.getRunCost();
        const canAfford = this.playerScrap >= cost || cost === 0;
        const color = canAfford ? 0x2ecc71 : 0x444455;

        this.startRunBg.clear();
        this.startRunBg.fillStyle(color, 1);
        this.startRunBg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
        this.startRunLabel.setText(cost > 0 ? `START ${cost}` : 'START RUN');
    }

    private createControlButton(
        x: number, y: number, text: string, width: number, height: number, color: number, callback: () => void
    ): void {
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);

        const label = this.add.text(0, 0, text, {
            fontFamily: 'monospace', fontSize: this.fonts.large, color: '#ffffff', fontStyle: 'bold',
        });
        label.setOrigin(0.5);

        const container = this.add.container(x, y, [bg, label]);
        container.setSize(width, height);
        container.setInteractive({ useHandCursor: true });

        container.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(color, 0.7);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        container.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(-width / 2, -height / 2, width, height, 8);
        });

        container.on('pointerdown', callback);
        this.controlsContainer.add(container);
    }

    private async startRun(): Promise<void> {
        const hasFittedModule = this.fittedModules.some(fm => fm.module !== null);

        if (!hasFittedModule) {
            const warning = this.add.text(
                GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, 'EQUIP AT LEAST ONE MODULE!',
                { fontFamily: 'monospace', fontSize: this.fonts.large, color: '#e74c3c', fontStyle: 'bold', }
            );
            warning.setOrigin(0.5);
            this.tweens.add({
                targets: warning, alpha: 0, duration: 1500, ease: 'Cubic.easeIn',
                onComplete: () => warning.destroy(),
            });
            return;
        }

        // Check if player can afford the run cost
        const cost = this.getRunCost();
        if (this.playerScrap < cost) {
            const warning = this.add.text(
                GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, `NEED ${cost} SCRAP!`,
                { fontFamily: 'monospace', fontSize: this.fonts.large, color: '#e74c3c', fontStyle: 'bold', }
            );
            warning.setOrigin(0.5);
            this.tweens.add({
                targets: warning, alpha: 0, duration: 1500, ease: 'Cubic.easeIn',
                onComplete: () => warning.destroy(),
            });
            return;
        }

        // Deduct cost and save threat level (for logged-in players)
        if (!this.isGuest && cost > 0) {
            try {
                const result = await apiClient.startRun(this.threatLevel);
                this.playerScrap = result.scrap;
                this.updateScrapDisplay();
                this.updateStartRunButton();
                this.updateRerollButton();
            } catch (error) {
                console.error('Failed to start run:', error);
                const warning = this.add.text(
                    GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, 'FAILED TO START RUN',
                    { fontFamily: 'monospace', fontSize: this.fonts.large, color: '#e74c3c', fontStyle: 'bold', }
                );
                warning.setOrigin(0.5);
                this.tweens.add({
                    targets: warning, alpha: 0, duration: 1500, ease: 'Cubic.easeIn',
                    onComplete: () => warning.destroy(),
                });
                return;
            }
        } else if (!this.isGuest) {
            // Free run at threat level 1 - just save the threat level
            apiClient.startRun(this.threatLevel).catch(err => console.error('Failed to save threat level:', err));
        }

        this.scene.start('GameScene', {
            threatLevel: this.threatLevel,
            fittedModules: this.fittedModules,
            runPlan: this.runPlan,
            isGuest: this.isGuest,
            activeMutators: Array.from(this.selectedMutators),
        });
    }

    private async rerollMap(): Promise<void> {
        const cost = this.getRerollCost();
        if (this.playerScrap < cost) {
            return; // Can't afford
        }

        // Deduct scrap locally
        this.playerScrap -= cost;
        this.updateScrapDisplay();
        this.updateRerollButton();

        // Save to server (don't block UI)
        if (!this.isGuest) {
            apiClient.updateScrap(-cost).catch(err => console.error('Failed to save reroll cost:', err));
        }

        this.tweens.add({
            targets: this.mapContainer, alpha: 0, duration: 150,
            onComplete: () => {
                // Regenerate the run with current threat level (new seed)
                this.runPlan = generateRun(this.threatLevel);
                this.updateMapPreview();
                this.tweens.add({ targets: this.mapContainer, alpha: 1, duration: 150 });
            },
        });
    }
}
